import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import "./KhubChatbot.css";
import { RemoteControlBanner } from "./RemoteControlBanner";
import { RemoteCursor } from "./RemoteCursor";
import { createRemoteControlExecutor } from "./remoteControlExecutor";
import {
  computeControlRegion,
  normalizeSurface,
  regionsEqual,
} from "./screenShareSurface";
import {
  publishLauncherState,
  registerLauncherToggle,
} from "./chatbotLauncherBridge";
import { firstUnread, newsSnippet, siblingBroadcast } from "./newsAlert";

// ── Customer notification helpers ────────────────────────────────────────────
let _audioCtx = null;
// Browsers suspend AudioContext until user interaction — resume on first click/key
function _ensureAudioCtx() {
  if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (_audioCtx.state === "suspended") _audioCtx.resume();
  return _audioCtx;
}
if (typeof window !== "undefined") {
  const _unlockAudio = () => {
    _ensureAudioCtx();
    document.removeEventListener("click", _unlockAudio);
    document.removeEventListener("keydown", _unlockAudio);
  };
  document.addEventListener("click", _unlockAudio, { once: true });
  document.addEventListener("keydown", _unlockAudio, { once: true });
}
function playCustomerBeep() {
  try {
    const ctx = _ensureAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.value = 660;
    gain.gain.value = 0.3;
    osc.start();
    osc.stop(ctx.currentTime + 0.15);
    setTimeout(() => {
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.type = "sine";
      osc2.frequency.value = 880;
      gain2.gain.value = 0.3;
      osc2.start();
      osc2.stop(ctx.currentTime + 0.15);
    }, 170);
  } catch { /* best-effort */ }
}

function showCustomerNotification(title, body) {
  if (!("Notification" in window)) return;
  if (Notification.permission === "granted") {
    new Notification(title, { body, icon: "/media/logos/khub-logo-light.png", tag: "khub-cs-msg" });
  } else if (Notification.permission !== "denied") {
    Notification.requestPermission().then((perm) => {
      if (perm === "granted") {
        new Notification(title, { body, icon: "/media/logos/khub-logo-light.png", tag: "khub-cs-msg" });
      }
    });
  }
}

// ── Markdown → safe HTML ─────────────────────────────────────────────────────
function parseMarkdown(text) {
  if (!text) return "";
  let s = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  s = s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");
  const lines = s.split("\n");
  const out = [];
  let inOL = false, inUL = false;
  const closeList = () => {
    if (inOL) { out.push("</ol>"); inOL = false; }
    if (inUL) { out.push("</ul>"); inUL = false; }
  };
  for (const line of lines) {
    const trimmed = line.trim();
    const h2 = trimmed.match(/^##\s+(.+)$/);
    const h1 = trimmed.match(/^#\s+(.+)$/);
    const olItem = trimmed.match(/^(\d+)\.\s+(.+)$/);
    const ulItem = trimmed.match(/^[-*]\s+(.+)$/);
    if (h1 || h2) { closeList(); out.push(`<h4>${(h1 || h2)[1]}</h4>`); }
    else if (olItem) { if (inUL) { out.push("</ul>"); inUL = false; } if (!inOL) { out.push("<ol>"); inOL = true; } out.push(`<li>${olItem[2]}</li>`); }
    else if (ulItem) { if (inOL) { out.push("</ol>"); inOL = false; } if (!inUL) { out.push("<ul>"); inUL = true; } out.push(`<li>${ulItem[1]}</li>`); }
    else if (trimmed === "") { closeList(); out.push("<br/>"); }
    else { closeList(); out.push(`<p>${trimmed}</p>`); }
  }
  closeList();
  return out.join("");
}

// ── Constants ────────────────────────────────────────────────────────────────
// Read from import.meta.env (Vite exposes REACT_APP_*-prefixed vars there via
// envPrefix in vite.config). NOTE: this app's `process.env.REACT_APP_*` define
// replacement does NOT reach source .js files (the treat-js-files-as-jsx plugin
// transforms them before define runs), so process.env would be empty here and
// the widget could never reach the CS Portal. import.meta.env is reliable.
const CS_PORTAL_URL =
  (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.REACT_APP_CS_PORTAL_URL) ||
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_CS_PORTAL_URL) ||
  "";
const TYPING_DEBOUNCE_MS = 1500;

const CS_WS_URL = (CS_PORTAL_URL || window.location.origin).replace(/^http/, "ws");

// Prefix relative /cs-api/... media URLs with the CS Portal origin so images
// and attachments resolve correctly when the widget is embedded in kapp.
function resolveMediaUrl(url) {
  if (!url) return url;
  if (url.startsWith("/")) return `${CS_PORTAL_URL}${url}`;
  return url;
}

// Attachment links carry the original filename so the CS Portal can serve a
// Content-Disposition with it — otherwise downloads land as the uuid storage key.
function resolveAttachmentUrl(att) {
  const base = resolveMediaUrl(att.url);
  if (!base || !att.name) return base;
  return `${base}${base.includes("?") ? "&" : "?"}name=${encodeURIComponent(att.name)}`;
}

const COMMON_EMOJIS = [
  "😊","😂","❤️","👍","👋","🎉","🔥","✨",
  "😭","🤔","😅","🙏","👏","⭐","💯","🚀",
  "😍","🥺","😎","🤩","😢","😡","🤗","😴",
  "🙌","💪","👀","🎊","💬","📎","✅","❌",
  "🛒","📦","💳","🏷️","🔍","📊","⚡","🌟",
];

const DEFAULT_FAQ = [
  { id: "d1", question: "How to create a Brand?", category: "platform" },
  { id: "d2", question: "How to create a Sale Order?", category: "orders" },
  { id: "d3", question: "How to get daily reports?", category: "reporting" },
  { id: "d4", question: "How to add Inventory?", category: "inventory" },
];

// ── Escalation trigger detection ──────────────────────────────────────────────
const ESCALATION_PATTERNS = [
  /talk\s+to\s+(a\s+)?(human|agent|person|representative)/i,
  /connect\s+(to\s+|with\s+)?(human|agent|customer\s+service|support|someone)/i,
  /need\s+(more\s+)?help/i,
  /speak\s+to\s+(a\s+)?(human|agent|person)/i,
  /human\s+(agent|support)/i,
  /live\s+(chat|agent|support)/i,
  /customer\s+service/i,
  /real\s+person/i,
  /not\s+(helpful|helping)/i,
  /don'?t\s+understand/i,
];

function isEscalationTrigger(text) {
  return ESCALATION_PATTERNS.some((r) => r.test(text));
}

// ── Auth helpers ──────────────────────────────────────────────────────────────
function getAuthToken(user, authToken) {
  return (
    authToken ||
    user?.entity?.auth_token ||
    user?.auth_token ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    null
  );
}

function authHeaders(user, authToken) {
  const token = getAuthToken(user, authToken);
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function authHeadersForm(user, authToken) {
  const token = getAuthToken(user, authToken);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getUserInitials(user) {
  const name = user?.entity?.first_name || user?.entity?.username || user?.entity?.login || "U";
  return name.charAt(0).toUpperCase();
}

function getUserFirstName(user) {
  return user?.entity?.first_name || user?.entity?.username || user?.entity?.login || "there";
}

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const IconChat = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const IconClose = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconMinimize = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="6" y1="12" x2="18" y2="12" />
  </svg>
);
const IconSend = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const IconHome = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const IconMessages = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const IconAttach = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);
const IconChevron = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const IconPower = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
    <line x1="12" y1="2" x2="12" y2="12" />
  </svg>
);
const IconNews = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h13a2 2 0 0 1 2 2v13a2 2 0 0 0 2 2H6a2 2 0 0 1-2-2z" />
    <line x1="8" y1="9" x2="15" y2="9" />
    <line x1="8" y1="13" x2="15" y2="13" />
    <line x1="8" y1="17" x2="12" y2="17" />
  </svg>
);
const IconBack = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const IconRecent = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

// ── Main Widget ───────────────────────────────────────────────────────────────
const KhubChatbotWidget = ({ user, authToken }) => {
  const [isOpen, setIsOpen] = useState(false);
  // Minimized = panel hidden but a small bubble stays docked at the bottom so
  // the user can pop the conversation back open. Distinct from closed (X), which
  // returns to the topbar help icon. minimized only matters while !isOpen.
  const [minimized, setMinimized] = useState(false);
  const [view, setView] = useState("tabs");           // tabs | chat | live
  const [activeTab, setActiveTab] = useState("home"); // home | news | messages | recent
  const [showConsent, setShowConsent] = useState(false);
  const [consentDone, setConsentDone] = useState(() =>
    localStorage.getItem("khub_chat_consent") === "1"
  );

  // Bot config & FAQ
  const [config, setConfig] = useState(null);
  const [faqQuestions, setFaqQuestions] = useState([]);

  // Chatbot state
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [feedback, setFeedback] = useState({}); // { [messageId]: "up" | "down" }
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [escalated, setEscalated] = useState(false);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [chatEnded, setChatEnded] = useState(false);
  const [confirmLiveEnd, setConfirmLiveEnd] = useState(false);
  const [showEscalateBar, setShowEscalateBar] = useState(false);

  // Live chat state
  const [livePhase, setLivePhase] = useState("idle"); // idle | async | waiting | active | ended
  const [liveSessionId, setLiveSessionId] = useState(null);
  const [liveMessages, setLiveMessages] = useState([]);
  // Currently-assigned live-chat agent. Drives the small "you're chatting
  // with X" header pinned at the top of the live view. Populated from session
  // restore and WS events (AGENT_INITIATED_CHAT / AGENT_ACCEPTED_CHAT) and
  // cleared whenever the live session resets.
  const [liveAgent, setLiveAgent] = useState(null); // { id, name, avatarUrl } | null
  const [botHistory, setBotHistory] = useState([]);
  const [showBotHistory, setShowBotHistory] = useState(false);
  const [liveInput, setLiveInput] = useState("");
  const [agentTyping, setAgentTyping] = useState(false);
  const [queueInfo, setQueueInfo] = useState(null);
  const [liveError, setLiveError] = useState(null);
  // Surfaced after the customer has been waiting ≥ 2 minutes — either in the
  // live queue, or in an active chat where they sent a message that the
  // agent hasn't replied to (covers the outbound case where the agent who
  // initiated the chat has gone idle). Offers them the choice to keep
  // waiting or switch to the existing offline (async) flow so an agent can
  // pick it up later.
  const [showLongWaitPrompt, setShowLongWaitPrompt] = useState(false);
  // Timestamp (ms) of the first user message in an active live chat that
  // hasn't been answered yet. Set on send, cleared when an agent message
  // arrives. Drives the 2-min long-wait prompt while in active mode.
  const [userMsgPendingAt, setUserMsgPendingAt] = useState(null);

  // ── Screen share / voice call (WebRTC) — customer side ─────────────────────
  // status: idle | awaitingConsent | connecting | live | ended | error
  // mode:   "screen" — one-way display capture (legacy, agent only views)
  //         "voice"  — bidirectional audio only (no screen)
  //         "both"   — display capture + bidirectional audio
  const [screenShareStatus, setScreenShareStatus] = useState("idle");
  const [screenShareMode, setScreenShareMode] = useState("screen");
  const [screenShareAgentName, setScreenShareAgentName] = useState(null);
  const [screenShareError, setScreenShareError] = useState(null);
  const [screenShareIsMuted, setScreenShareIsMuted] = useState(false);
  const [screenShareRemoteStream, setScreenShareRemoteStream] = useState(null);
  const remoteAudioRef = useRef(null);
  const screenSharePcRef = useRef(null);
  const screenShareLocalStreamRef = useRef(null);   // display capture (screen)
  const screenShareLocalMicRef = useRef(null);      // getUserMedia mic stream
  // Last published { surface, region } plus the timers keeping it fresh —
  // the agent needs it to map pointer coords back into this viewport.
  const screenShareSurfaceRef = useRef(null);
  const screenShareSurfaceTimerRef = useRef(null);
  const screenShareSurfaceListenerRef = useRef(null);

  // ── Remote control (co-browsing) — customer side ──────────────────────────
  // status: idle | requesting | active   (the agent is asking, or driving)
  const [remoteControlStatus, setRemoteControlStatus] = useState("idle");
  const [remoteCursor, setRemoteCursor] = useState(null);
  const remoteControlChannelRef = useRef(null);
  const remoteControlExecutorRef = useRef(null);
  const remoteControlActiveRef = useRef(false);

  // Async support flow state (no agents online)
  // asyncStep: askIssue | askPriority | submitting | submitted
  const [asyncStep, setAsyncStep] = useState(null);
  const [asyncMessages, setAsyncMessages] = useState([]);
  const [asyncIssue, setAsyncIssue] = useState("");
  const [asyncUrgent, setAsyncUrgent] = useState(null);
  const [asyncInput, setAsyncInput] = useState("");
  const [agentArrivedBadge, setAgentArrivedBadge] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Toast shown when an agent joins / sends a message and the user is on a
  // non-live view inside an open widget. Structure: { agentName, preview, unread }
  const [agentAlert, setAgentAlert] = useState(null);
  // Home-tab banner shown when an agent reaches out (initiates / reopens) and the
  // user has not yet accepted or ignored. Structure:
  //   { sessionId, agentName, preview, reopened, acknowledging }
  // While this is set the bubble click lands on Home (not live), and the live
  // session is "ready" — accepting just flips the view; ignoring keeps the
  // session active so the agent can keep typing.
  const [agentReachOut, setAgentReachOut] = useState(null);

  // Soft-close marker for the live chat. When the agent clicks "End Chat"
  // on a Read-later session (user_acknowledgement = "ignored"), the backend
  // does NOT close the session — it just records agent_ended_at. The user
  // still owns the chat. We mirror that timestamp here so the live view
  // can show the "Agent stepped away → Chat now" CTA instead of the normal
  // input bar. Cleared whenever the user opens a fresh session.
  const [liveAgentEndedAt, setLiveAgentEndedAt] = useState(null);
  // After the user clicks "Chat now" we branch on agent availability:
  //   "live"  → agents online, normal input bar enabled
  //   "async" → no agents online, single textarea + send-and-close
  // Null while we're still showing the Chat-now button.
  const [softCloseMode, setSoftCloseMode] = useState(null);
  const [softCloseAsyncInput, setSoftCloseAsyncInput] = useState("");
  const [softCloseSubmitting, setSoftCloseSubmitting] = useState(false);

  // Recent sessions list shown on Home tab so the user can resume any prior
  // conversation. Populated on widget open from /live-chat/sessions/recent.
  const [recentSessions, setRecentSessions] = useState([]);

  // Shared media state
  const [showEmoji, setShowEmoji] = useState(false);
  const [uploading, setUploading] = useState(false);

  // K-News (broadcast announcements) state
  const [broadcasts, setBroadcasts] = useState([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const newsWsRef = useRef(null);
  // Attention flow for a broadcast that lands while the panel is closed:
  // newsNag = docked bubble drops in + keeps hopping until clicked;
  // newsAlert = preview card above the bubble (title/body/"Read now").
  const [newsNag, setNewsNag] = useState(false);
  const [newsAlert, setNewsAlert] = useState(null);
  const raiseNewsAlert = useCallback((items) => {
    if (isOpenRef.current) return;
    const bc = firstUnread(items);
    if (!bc) return;
    setNewsAlert(bc);
    setNewsNag(true);
    playCustomerBeep();
  }, []);

  const messagesEndRef = useRef(null);
  const liveEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const liveWsRef = useRef(null);
  // Holds the id of the live session that was just closed, so the Reopen
  // button still has something to POST against after CHAT_ENDED nulls
  // liveSessionId (the null is needed to let an agent re-init the SAME id
  // via AGENT_INITIATED_CHAT remount the WS effect).
  const lastClosedSessionIdRef = useRef(null);
  const typingTimer = useRef(null);
  const queuePollRef = useRef(null);
  const hasRestoredRef = useRef(false);
  const isOpenRef = useRef(false);
  const viewRef = useRef("tabs");

  const livePhaseRef = useRef(livePhase);
  const liveSessionIdRef = useRef(liveSessionId);

  // Keep refs in sync so WS callbacks can read the latest values without
  // re-subscribing the socket on every render
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { livePhaseRef.current = livePhase; }, [livePhase]);
  useEffect(() => { liveSessionIdRef.current = liveSessionId; }, [liveSessionId]);
  useEffect(() => {
    viewRef.current = view;
    // Clear the "agent is here" toast as soon as the user enters the live view
    if (view === "live") setAgentAlert(null);
  }, [view]);

  // ── Fetch bot config ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${CS_PORTAL_URL}/cs-api/chatbot/config`, {
          headers: authHeaders(user, authToken),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.is_enabled) setConfig(data);
      } catch { /* CS Portal unreachable */ }
    };
    fetchConfig();
  }, [user, authToken]);

  // ── Fetch FAQ questions ───────────────────────────────────────────────────
  useEffect(() => {
    if (!config) return;
    fetch(`${CS_PORTAL_URL}/cs-api/chatbot/faq-questions`, {
      headers: authHeaders(user, authToken),
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data?.length) setFaqQuestions(data); })
      .catch(() => {});
  }, [config, user, authToken]);

  // ── K-News: fetch broadcasts + unseen count ──────────────────────────────
  const fetchBroadcasts = useCallback(async () => {
    const token = getAuthToken(user, authToken);
    if (!token) return;
    setNewsLoading(true);
    try {
      const r = await fetch(`${CS_PORTAL_URL}/cs-api/v1/broadcasts`, {
        headers: authHeaders(user, authToken),
      });
      if (r.ok) {
        const data = await r.json();
        setBroadcasts(data.items || []);
        setUnseenCount(data.unseen_count || 0);
        return data.items || [];
      }
    } catch { /* CS Portal unreachable */ }
    finally { setNewsLoading(false); }
    return [];
  }, [user, authToken]);

  const fetchUnseenCount = useCallback(async () => {
    const token = getAuthToken(user, authToken);
    if (!token) return;
    try {
      const r = await fetch(`${CS_PORTAL_URL}/cs-api/v1/broadcasts/unseen-count`, {
        headers: authHeaders(user, authToken),
      });
      if (r.ok) {
        const data = await r.json();
        setUnseenCount(data.unseen_count || 0);
        return data.unseen_count || 0;
      }
    } catch { /* ignore */ }
    return 0;
  }, [user, authToken]);

  // Initial count on mount + whenever widget is opened (keeps badge fresh).
  // First load of a browser session with unread broadcasts also raises the
  // bubble/preview nag once — not on every page navigation.
  useEffect(() => {
    if (!config) return;
    fetchUnseenCount().then((n) => {
      if (!n || sessionStorage.getItem("khub_news_nagged")) return;
      sessionStorage.setItem("khub_news_nagged", "1");
      fetchBroadcasts().then(raiseNewsAlert);
    });
  }, [config, fetchUnseenCount, fetchBroadcasts, raiseNewsAlert]);

  useEffect(() => {
    if (!config || !isOpen) return;
    fetchBroadcasts();
  }, [config, isOpen, fetchBroadcasts]);

  // WebSocket push — wake up when an agent publishes a new broadcast
  useEffect(() => {
    if (!config) return;
    const token = getAuthToken(user, authToken);
    if (!token) return;

    let ws;
    try {
      ws = new WebSocket(`${CS_WS_URL}/cs-api/ws/broadcasts?token=${token}`);
    } catch { return; }

    newsWsRef.current = ws;
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.event === "BROADCAST_NEW") {
          // Lightweight — just refetch to pick up the new row with full body,
          // then nag (bubble hop + preview card) if the panel is closed.
          fetchBroadcasts().then(raiseNewsAlert);
        }
      } catch { /* ignore malformed */ }
    };
    ws.onerror = () => { /* widget falls back to poll on open */ };

    return () => {
      try { ws.close(); } catch { /* ignore */ }
      newsWsRef.current = null;
    };
  }, [config, user, authToken, fetchBroadcasts, raiseNewsAlert]);

  const markBroadcastRead = useCallback(async (bc) => {
    if (bc.is_read) return;
    try {
      await fetch(`${CS_PORTAL_URL}/cs-api/v1/broadcasts/${bc.id}/read`, {
        method: "POST",
        headers: authHeaders(user, authToken),
      });
      setBroadcasts((prev) =>
        prev.map((b) => (b.id === bc.id ? { ...b, is_read: true } : b))
      );
      setUnseenCount((n) => Math.max(0, n - 1));
    } catch { /* ignore */ }
  }, [user, authToken]);

  const acknowledgeBroadcast = useCallback(async (bc) => {
    try {
      await fetch(`${CS_PORTAL_URL}/cs-api/v1/broadcasts/${bc.id}/acknowledge`, {
        method: "POST",
        headers: authHeaders(user, authToken),
      });
      setBroadcasts((prev) =>
        prev.map((b) =>
          b.id === bc.id ? { ...b, is_read: true, is_acknowledged: true } : b,
        ),
      );
      setSelectedBroadcast((prev) =>
        prev && prev.id === bc.id
          ? { ...prev, is_read: true, is_acknowledged: true }
          : prev,
      );
      setUnseenCount((n) => Math.max(0, n - (bc.is_read ? 0 : 1)));
    } catch { /* ignore */ }
  }, [user, authToken]);

  const openBroadcast = (bc) => {
    setSelectedBroadcast(bc);
    markBroadcastRead(bc);
  };

  // ── Remember the assigned agent so the live view can show their identity ─
  // Used by the small "chatting with X" header pinned at the top of the
  // live view. Called whenever an agent payload arrives (session restore,
  // AGENT_INITIATED_CHAT, AGENT_ACCEPTED_CHAT). Skips no-op updates.
  const updateLiveAgent = useCallback((agent) => {
    if (!agent || agent.id == null) return;
    const next = {
      id: agent.id,
      name: agent.name || "Agent",
      avatarUrl: resolveMediaUrl(agent.avatar_url) || null,
    };
    setLiveAgent((prev) => {
      if (prev && prev.id === next.id && prev.name === next.name && prev.avatarUrl === next.avatarUrl) {
        return prev;
      }
      return next;
    });
  }, []);

  // ── Acknowledge an agent reach-out (Accept / Read later on Home) ─────────
  // - "accept" (Chat now / Read message): clear the Home card and jump to
  //   the live view. The session moves to Recent server-side.
  // - "ignore" (Read later): keep the card pinned on Home in a "deferred"
  //   state so the user can come back and read whenever they want. The
  //   banner stays until they actually open the chat (which then triggers
  //   a follow-up "accept" through this same path).
  const acknowledgeReachOut = useCallback(async (action) => {
    setAgentReachOut((prev) => {
      if (!prev || prev.acknowledging) return prev;
      const sessionId = prev.sessionId;
      // Fire-and-forget the server call. We optimistically apply the UI
      // change so the banner reflects the new state even if the network is
      // flaky — the agent-side state will reconcile on their next message.
      (async () => {
        try {
          await fetch(
            `${CS_PORTAL_URL}/cs-api/live-chat/sessions/${sessionId}/acknowledge`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...authHeaders(user, authToken),
              },
              body: JSON.stringify({ action }),
            },
          );
        } catch { /* best-effort */ }
      })();
      if (action === "accept") return null;          // card is consumed
      return { ...prev, acknowledged: "ignored" };   // pin Read-later card
    });
    if (action === "accept") {
      setView("live");
      setAgentAlert(null);
    }
  }, [user, authToken]);

  // ── Fetch recent sessions (Recent tab "Recent conversations" list) ───────
  // Backend returns agent-involved sessions from the last 45 days.
  const fetchRecentSessions = useCallback(async () => {
    try {
      const r = await fetch(
        `${CS_PORTAL_URL}/cs-api/live-chat/sessions/recent`,
        { headers: authHeaders(user, authToken) },
      );
      if (r.ok) setRecentSessions(await r.json());
    } catch { /* ignore */ }
  }, [user, authToken]);

  // Refresh recent sessions every time the widget opens to the Recent tab so
  // a chat that just ended elsewhere shows up without a hard reload.
  useEffect(() => {
    if (!isOpen) return;
    if (view !== "tabs" || activeTab !== "recent") return;
    fetchRecentSessions();
  }, [isOpen, view, activeTab, fetchRecentSessions]);

  // Resume an old session from the Home recents list. Active/waiting → jump
  // into live view; ended sessions just show the transcript via setLive*.
  const resumeRecentSession = useCallback(async (s) => {
    try {
      const [mr, br] = await Promise.all([
        fetch(`${CS_PORTAL_URL}/cs-api/live-chat/sessions/${s.id}/messages`, {
          headers: authHeaders(user, authToken),
        }),
        fetch(`${CS_PORTAL_URL}/cs-api/live-chat/sessions/${s.id}/bot-history`, {
          headers: authHeaders(user, authToken),
        }).catch(() => null),
      ]);
      if (mr.ok) setLiveMessages(await mr.json());
      if (br && br.ok) setBotHistory(await br.json());
      setLiveSessionId(s.id);
      if (s.status === "active" || s.status === "waiting") {
        setLivePhase(s.status === "waiting" ? "waiting" : "active");
        localStorage.setItem("khub_live_session_id", String(s.id));
      } else {
        setLivePhase("ended");
        setChatEnded(true);
      }
      setView("live");
    } catch { /* ignore */ }
  }, [user, authToken]);

  // ── Restore active session from localStorage on mount ────────────────────
  useEffect(() => {
    if (hasRestoredRef.current) return;
    const token = getAuthToken(user, authToken);
    if (!token) return;
    hasRestoredRef.current = true;

    const restore = async () => {
      const savedLiveId = localStorage.getItem("khub_live_session_id");
      if (savedLiveId) {
        try {
          const r = await fetch(`${CS_PORTAL_URL}/cs-api/live-chat/sessions/${savedLiveId}`, {
            headers: authHeaders(user, authToken),
          });
          if (r.ok) {
            const liveSess = await r.json();
            if (liveSess.status === "waiting" || liveSess.status === "active") {
              const [mr, br] = await Promise.all([
                fetch(
                  `${CS_PORTAL_URL}/cs-api/live-chat/sessions/${savedLiveId}/messages`,
                  { headers: authHeaders(user, authToken) }
                ),
                fetch(
                  `${CS_PORTAL_URL}/cs-api/live-chat/sessions/${savedLiveId}/bot-history`,
                  { headers: authHeaders(user, authToken) }
                ).catch(() => null),
              ]);
              if (mr.ok) setLiveMessages(await mr.json());
              if (br && br.ok) setBotHistory(await br.json());
              setLiveSessionId(parseInt(savedLiveId, 10));
              // Async sessions in waiting status render the "submitted" confirmation view
              // instead of the queue UI (there's no position/ETA — an agent will follow up)
              if (liveSess.status === "waiting" && liveSess.is_async) {
                setLivePhase("async");
                setAsyncStep("submitted");
                setAsyncIssue(liveSess.initial_message || "");
                setAsyncUrgent(liveSess.priority === "high" || liveSess.priority === "urgent");
                setAsyncMessages([
                  {
                    role: "assistant",
                    content: "Your request has been submitted. An agent will follow up as soon as they're online.",
                    id: "async-restored",
                  },
                ]);
              } else {
                setLivePhase(liveSess.status === "waiting" ? "waiting" : "active");
              }
              // Soft-close marker survives reloads — hydrate it from the
              // restored session so the live view can render the
              // "Agent stepped away" state correctly.
              setLiveAgentEndedAt(liveSess.agent_ended_at || null);
              // If the agent reached out (initiated/reopened) and the user
              // hasn't actually read it yet, land on Home with the banner —
              // don't hijack the user into the live view on bubble click /
              // reload. "Hasn't read" covers two states:
              //   pending  → Accept / Read-later banner
              //   ignored  → persistent Read-later card (user deferred earlier)
              updateLiveAgent(liveSess.assigned_agent);
              const ack = liveSess.user_acknowledgement;
              if (ack === "pending" || ack === "ignored") {
                const agentName = liveSess.assigned_agent?.name || "An agent";
                const agentAvatar = resolveMediaUrl(liveSess.assigned_agent?.avatar_url) || null;
                setAgentReachOut({
                  sessionId: parseInt(savedLiveId, 10),
                  agentName,
                  agentAvatar,
                  preview: liveSess.initial_message
                    ? (liveSess.initial_message.length > 90
                      ? liveSess.initial_message.slice(0, 90) + "…"
                      : liveSess.initial_message)
                    : `${agentName} reached out — they're waiting for you.`,
                  reopened: !!liveSess.is_agent_initiated,
                  acknowledged: ack === "ignored" ? "ignored" : undefined,
                  agent_ended_at: liveSess.agent_ended_at || null,
                });
                setView("tabs");
                setActiveTab("home");
              } else {
                setView("live");
              }
              return;
            }
          }
        } catch { /* ignore */ }
        localStorage.removeItem("khub_live_session_id");
      }

      const savedBotId = localStorage.getItem("khub_bot_session_id");
      if (savedBotId) {
        try {
          const r = await fetch(`${CS_PORTAL_URL}/cs-api/chatbot/sessions/${savedBotId}`, {
            headers: authHeaders(user, authToken),
          });
          if (r.ok) {
            const botSess = await r.json();
            if (botSess.status === "active" || botSess.status === "escalated") {
              const mr = await fetch(
                `${CS_PORTAL_URL}/cs-api/chatbot/sessions/${savedBotId}/messages`,
                { headers: authHeaders(user, authToken) }
              );
              if (mr.ok) {
                const data = await mr.json();
                setMessages(data.items || []);
              }
              setSession(botSess);
              setView("chat");
              return;
            }
          }
        } catch { /* ignore */ }
        localStorage.removeItem("khub_bot_session_id");
      }
    };

    restore();
  }, [user, authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist session IDs to localStorage ──────────────────────────────────
  useEffect(() => {
    if (session?.id) localStorage.setItem("khub_bot_session_id", String(session.id));
  }, [session?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (liveSessionId) localStorage.setItem("khub_live_session_id", String(liveSessionId));
    else setLiveAgent(null);
  }, [liveSessionId]);

  // ── Auto-scroll ───────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    liveEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [liveMessages, agentTyping, asyncMessages, asyncStep]);

  // ── Focus input on chat view ───────────────────────────────────────────────
  useEffect(() => {
    if (view === "chat" && isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [view, isOpen]);

  // ── Chatbot WebSocket ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!session) return;
    const token = getAuthToken(user, authToken);
    const ws = new WebSocket(`${CS_WS_URL}/cs-api/ws/chatbot/${session.id}?token=${token || ""}`);
    wsRef.current = ws;
    return () => ws.close();
  }, [session, user, authToken]);

  // ── Screen share helpers ─────────────────────────────────────────────────
  // The agent is viewer-only; the customer is the offerer. Signaling rides the
  // existing live-chat WebSocket (`liveWsRef`).
  const sendScreenShareEvent = useCallback((type, payload = {}) => {
    const ws = liveWsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify({ type, ...payload }));
    return true;
  }, []);

  const teardownScreenShare = useCallback(() => {
    if (screenShareSurfaceTimerRef.current) {
      clearInterval(screenShareSurfaceTimerRef.current);
      screenShareSurfaceTimerRef.current = null;
    }
    if (screenShareSurfaceListenerRef.current) {
      window.removeEventListener("resize", screenShareSurfaceListenerRef.current);
      screenShareSurfaceListenerRef.current = null;
    }
    screenShareSurfaceRef.current = null;
    if (screenShareLocalStreamRef.current) {
      screenShareLocalStreamRef.current.getTracks().forEach((t) => t.stop());
      screenShareLocalStreamRef.current = null;
    }
    if (screenShareLocalMicRef.current) {
      screenShareLocalMicRef.current.getTracks().forEach((t) => t.stop());
      screenShareLocalMicRef.current = null;
    }
    if (screenSharePcRef.current) {
      try { screenSharePcRef.current.close(); } catch { /* ignore */ }
      screenSharePcRef.current = null;
    }
    // Tear down the remote-control data channel + executor alongside the
    // peer connection. The channel cannot outlive its parent pc.
    if (remoteControlChannelRef.current) {
      try { remoteControlChannelRef.current.close(); } catch { /* ignore */ }
      remoteControlChannelRef.current = null;
    }
    remoteControlExecutorRef.current = null;
    remoteControlActiveRef.current = false;
    setRemoteControlStatus("idle");
    setRemoteCursor(null);
    setScreenShareRemoteStream(null);
    setScreenShareIsMuted(false);
  }, []);

  // Toggle the local mic on/off without renegotiating — purely flips
  // track.enabled, so the agent hears silence while muted but the audio
  // transceiver stays up.
  const toggleScreenShareMute = useCallback(() => {
    const stream = screenShareLocalMicRef.current;
    if (!stream) return;
    const tracks = stream.getAudioTracks();
    if (!tracks.length) return;
    const nextEnabled = !tracks[0].enabled;
    tracks.forEach((t) => { t.enabled = nextEnabled; });
    setScreenShareIsMuted(!nextEnabled);
  }, []);

  const stopScreenShare = useCallback((reason = "manual") => {
    sendScreenShareEvent("SCREEN_SHARE_END", { reason });
    teardownScreenShare();
    setScreenShareStatus("ended");
  }, [sendScreenShareEvent, teardownScreenShare]);

  const declineScreenShare = useCallback((reason = "user_declined") => {
    sendScreenShareEvent("SCREEN_SHARE_DECLINE", { reason });
    setScreenShareStatus("ended");
    setScreenShareAgentName(null);
  }, [sendScreenShareEvent]);

  // ── Remote control (co-browsing) handlers ────────────────────────────────
  const acceptRemoteControl = useCallback(() => {
    if (!remoteControlChannelRef.current) return;
    // Build the executor on first grant; reuse it on subsequent grants in
    // the same session (data channel persists across grant/revoke cycles).
    if (!remoteControlExecutorRef.current) {
      remoteControlExecutorRef.current = createRemoteControlExecutor({
        isActive: () => remoteControlActiveRef.current,
        setRemoteCursor,
      });
    }
    remoteControlActiveRef.current = true;
    setRemoteControlStatus("active");
    sendScreenShareEvent("REMOTE_CONTROL_GRANT");
  }, [sendScreenShareEvent]);

  const declineRemoteControl = useCallback(() => {
    sendScreenShareEvent("REMOTE_CONTROL_DECLINE", { reason: "user_declined" });
    setRemoteControlStatus("idle");
  }, [sendScreenShareEvent]);

  const revokeRemoteControl = useCallback((reason = "manual") => {
    if (remoteControlActiveRef.current) {
      sendScreenShareEvent("REMOTE_CONTROL_REVOKE", { reason });
    }
    remoteControlActiveRef.current = false;
    setRemoteControlStatus("idle");
    setRemoteCursor(null);
  }, [sendScreenShareEvent]);

  // Publish which surface the customer picked and where this page's viewport
  // sits inside it, so the agent can project pointer coords back into this
  // document. Re-published on resize and on a slow poll — the platform gives
  // us no event for a window being dragged to a new position, and the customer
  // can switch surfaces mid-share (surfaceSwitching: "include").
  //
  // ponytail: 2s poll of six numbers. Cheap enough; replace it if a window-move
  // event ever ships.
  const startSurfacePublisher = useCallback((videoTrack) => {
    let ticks = 0;
    const publish = (force) => {
      if (!screenShareLocalStreamRef.current) return;
      const settings =
        typeof videoTrack.getSettings === "function" ? videoTrack.getSettings() : {};
      const surface = normalizeSurface(settings.displaySurface);
      const region = computeControlRegion(surface);
      const prev = screenShareSurfaceRef.current;
      if (
        !force && prev && prev.surface === surface && regionsEqual(prev.region, region)
      ) {
        return;
      }
      screenShareSurfaceRef.current = { surface, region };
      sendScreenShareEvent("SCREEN_SHARE_SURFACE", { surface, region });
    };

    publish(true);
    screenShareSurfaceTimerRef.current = setInterval(() => {
      ticks += 1;
      // Re-assert every ~10s so an agent who joined late (or a dropped frame
      // of signaling) still converges on the right region.
      publish(ticks % 5 === 0);
    }, 2000);
    screenShareSurfaceListenerRef.current = () => publish(true);
    window.addEventListener("resize", screenShareSurfaceListenerRef.current);
  }, [sendScreenShareEvent]);

  const acceptScreenShare = useCallback(async () => {
    setScreenShareError(null);
    setScreenShareStatus("connecting");
    // Mode is read from state at click time — the SCREEN_SHARE_REQUEST
    // handler set it from the agent's payload before this fires.
    const mode = screenShareMode || "screen";
    const wantScreen = mode === "screen" || mode === "both";
    const wantVoice = mode === "voice" || mode === "both";
    sendScreenShareEvent("SCREEN_SHARE_ACCEPT", { mode });
    try {
      // 1. Fetch ephemeral ICE config (TURN creds expire in ~10 min)
      const headers = authHeaders(user, authToken);
      const iceRes = await fetch(
        `${CS_PORTAL_URL}/cs-api/v1/live-chat/webrtc/ice-config?session_id=${liveSessionId}`,
        { headers }
      );
      if (!iceRes.ok) throw new Error("ICE config fetch failed");
      const iceData = await iceRes.json();
      const iceServers = (iceData.ice_servers || []).map((s) => ({
        urls: s.urls,
        username: s.username,
        credential: s.credential,
      }));

      // 2. Build the peer connection up front so we can attach handlers
      //    before any track addition / media prompt.
      const pc = new RTCPeerConnection({ iceServers });
      screenSharePcRef.current = pc;
      console.log("[SS] sharer mode =", mode, "ICE servers:", iceServers.map((s) => s.urls).flat());
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          console.log("[SS] sharer → local ICE", e.candidate.type, e.candidate.protocol, e.candidate.address || e.candidate.candidate);
          sendScreenShareEvent("SCREEN_SHARE_ICE", { candidate: e.candidate.toJSON() });
        } else {
          console.log("[SS] sharer → ICE gathering complete");
        }
      };
      pc.oniceconnectionstatechange = () => {
        console.log("[SS] sharer iceConnectionState =", pc.iceConnectionState);
      };
      pc.onconnectionstatechange = () => {
        console.log("[SS] sharer connectionState =", pc.connectionState);
        if (pc.connectionState === "connected") {
          setScreenShareStatus("live");
        }
        // "disconnected" is transient — ICE often recovers on its own. Only
        // the terminal states end the session; tearing down on a Wi-Fi blip
        // was dropping live support calls mid-conversation.
        if (["failed", "closed"].includes(pc.connectionState)) {
          teardownScreenShare();
          setScreenShareStatus((s) => (s === "ended" ? s : "ended"));
        }
      };
      // Inbound tracks — the agent's mic in voice/both modes. Combined into
      // one MediaStream so a single <audio> element plays everything.
      pc.ontrack = (e) => {
        console.log("[SS] sharer ontrack", e.track.kind, "streams:", e.streams.length);
        setScreenShareRemoteStream(e.streams[0] || new MediaStream([e.track]));
      };

      // 3. Capture media per mode. Voice can fail with NotAllowedError if
      //    the user blocks mic; screen can fail if they dismiss the picker.
      if (wantScreen) {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          // Hint the picker at the whole desktop: agents need to follow the
          // customer across tabs and apps, and a tab capture goes blank the
          // moment the customer looks anywhere else. The customer can still
          // narrow it down to a window or a single tab in the picker.
          video: { frameRate: 15, displaySurface: "monitor" },
          audio: false,
          selfBrowserSurface: "include",
          // Let them re-pick a different surface without restarting the call.
          surfaceSwitching: "include",
          monitorTypeSurfaces: "include",
        });
        screenShareLocalStreamRef.current = stream;
        const videoTrack = stream.getVideoTracks()[0];
        // Honour Chrome's native "Stop sharing" pill.
        videoTrack.onended = () => stopScreenShare("manual");
        stream.getTracks().forEach((t) => pc.addTrack(t, stream));
        startSurfacePublisher(videoTrack);
      }
      if (wantVoice) {
        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        screenShareLocalMicRef.current = micStream;
        micStream.getAudioTracks().forEach((t) => pc.addTrack(t, micStream));
      }

      // 3.5 Open a data channel for remote control. Created here on the
      //     sharer (customer) side so the m-line lands in the initial SDP
      //     offer — the agent picks it up via pc.ondatachannel after they
      //     answer. Sits idle until the agent asks for control and the
      //     customer grants it.
      const controlChannel = pc.createDataChannel("remote-control", { ordered: true });
      remoteControlChannelRef.current = controlChannel;
      controlChannel.onmessage = (msg) => {
        if (!remoteControlActiveRef.current) return;
        try {
          const evt = JSON.parse(msg.data);
          remoteControlExecutorRef.current?.apply(evt);
        } catch { /* ignore malformed payload */ }
      };
      controlChannel.onclose = () => {
        // If the channel dies mid-session, drop control state cleanly.
        remoteControlActiveRef.current = false;
        setRemoteControlStatus("idle");
        setRemoteCursor(null);
      };

      // 4. Build & send the SDP offer.
      const offer = await pc.createOffer({
        offerToReceiveAudio: wantVoice,
        offerToReceiveVideo: false,
      });
      await pc.setLocalDescription(offer);
      sendScreenShareEvent("SCREEN_SHARE_OFFER", { sdp: offer.sdp, mode });
    } catch (err) {
      console.error("[SS] sharer acceptScreenShare failed:", {
        name: err?.name,
        message: err?.message,
        mode,
        wantScreen,
        wantVoice,
        liveSessionId,
        err,
      });
      if (err?.name === "NotAllowedError") {
        // Picker dismissed or mic blocked — treat as a soft decline.
        sendScreenShareEvent("SCREEN_SHARE_DECLINE", { reason: "permission_denied" });
        setScreenShareStatus("ended");
        teardownScreenShare();
      } else {
        setScreenShareError(err?.message || "Session failed");
        setScreenShareStatus("error");
        teardownScreenShare();
      }
    }
    setScreenShareAgentName(null);
  }, [liveSessionId, user, authToken, screenShareMode, sendScreenShareEvent, teardownScreenShare, stopScreenShare, startSurfacePublisher]);

  // Tear down WebRTC if the live chat session goes away.
  useEffect(() => () => teardownScreenShare(), [teardownScreenShare]);

  // ESC always revokes active remote control. Captured at the document root
  // so an agent-driven focus inside an input can't swallow it.
  useEffect(() => {
    if (remoteControlStatus !== "active") return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        revokeRemoteControl("esc");
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [remoteControlStatus, revokeRemoteControl]);

  // Surface media-session lifecycle into the live chat transcript so the
  // user sees mode-aware "Voice call / Screen sharing started/ended" notices
  // alongside the queue and chat-ended messages.
  const screenShareLifecycleRef = useRef("idle");
  useEffect(() => {
    const prev = screenShareLifecycleRef.current;
    screenShareLifecycleRef.current = screenShareStatus;
    if (prev === screenShareStatus) return;
    const wasActive = prev === "live" || prev === "connecting";
    const nowEnded = screenShareStatus === "ended"
      || screenShareStatus === "error"
      || screenShareStatus === "idle";
    const label =
      screenShareMode === "voice" ? "Voice call" :
      screenShareMode === "both" ? "Voice call + screen share" :
      "Screen sharing";
    if (screenShareStatus === "live" && prev !== "live") {
      setLiveMessages((m) => [...m, {
        id: `ss-start-${Date.now()}`, sender_type: "system", sender_name: "System",
        message: `${label} started.`, message_type: "text",
        created_at: new Date().toISOString(),
      }]);
    } else if (wasActive && nowEnded) {
      setLiveMessages((m) => [...m, {
        id: `ss-end-${Date.now()}`, sender_type: "system", sender_name: "System",
        message: `${label} ended.`, message_type: "text",
        created_at: new Date().toISOString(),
      }]);
    }
  }, [screenShareStatus, screenShareMode]);

  // ── Live chat WebSocket ───────────────────────────────────────────────────
  useEffect(() => {
    if (!liveSessionId) return;
    const token = getAuthToken(user, authToken);
    const ws = new WebSocket(`${CS_WS_URL}/cs-api/ws/live-chat/session/${liveSessionId}?token=${token || ""}`);
    liveWsRef.current = ws;
    ws.onmessage = (evt) => {
      try {
        const event = JSON.parse(evt.data);
        switch (event.event) {
          case "AGENT_ACCEPTED_CHAT": {
            setLivePhase("active");
            clearInterval(queuePollRef.current);
            updateLiveAgent(event.agent);
            const agentName = event.agent?.name || "An agent";
            const agentAvatar = resolveMediaUrl(event.agent?.avatar_url) || null;
            // Hydrate the transcript with everything already stored on the
            // server (the user's initial_message, join-system message, etc.)
            // so the user sees their own history — not just the agent's new
            // messages. Merge with any local messages to avoid races with
            // NEW_MESSAGE events arriving during the fetch.
            (async () => {
              try {
                const [mr, br] = await Promise.all([
                  fetch(
                    `${CS_PORTAL_URL}/cs-api/live-chat/sessions/${liveSessionId}/messages`,
                    { headers: authHeaders(user, authToken) }
                  ),
                  fetch(
                    `${CS_PORTAL_URL}/cs-api/live-chat/sessions/${liveSessionId}/bot-history`,
                    { headers: authHeaders(user, authToken) }
                  ).catch(() => null),
                ]);
                if (mr.ok) {
                  const history = await mr.json();
                  setLiveMessages((prev) => {
                    const seen = new Set();
                    const merged = [];
                    for (const m of [...history, ...prev]) {
                      if (seen.has(m.id)) continue;
                      seen.add(m.id);
                      merged.push(m);
                    }
                    merged.sort((a, b) => (a.id || 0) - (b.id || 0));
                    return merged;
                  });
                }
                if (br && br.ok) setBotHistory(await br.json());
              } catch { /* ignore — NEW_MESSAGE events will still populate */ }
            })();
            // Note: we intentionally don't append a client-side "joined" message here —
            // the server already persists one, and the history fetch above brings it in
            // at the correct position, avoiding a duplicate.
            // Pulse the launcher when the widget is closed so the user notices
            if (!isOpenRef.current) {
              setAgentArrivedBadge(true);
              setUnreadCount((c) => c + 1);
              playCustomerBeep();
              if (document.hidden) showCustomerNotification("Agent available", "An agent has joined your chat");
            }
            // Show an in-widget toast when the widget is open but the user is
            // elsewhere (home, bot chat) so they know to jump into the chat
            if (isOpenRef.current && viewRef.current !== "live") {
              setAgentAlert({
                agentName,
                agentAvatar,
                preview: "Agent is available — tap to start chatting.",
                unread: 0,
              });
            }
            break;
          }
          case "NEW_MESSAGE": {
            setLiveMessages((prev) =>
              prev.some((m) => m.id === event.message.id) ? prev : [...prev, event.message]
            );
            const isAgentMsg = event.message?.sender_type === "agent";
            if (isAgentMsg) {
              // Agent replied — drop the unanswered-turn marker and any
              // long-wait prompt that may already be visible.
              setUserMsgPendingAt(null);
              setShowLongWaitPrompt(false);
              // Not viewing the live chat → surface an attention signal
              const userIsWatching = isOpenRef.current && viewRef.current === "live";
              if (!userIsWatching) {
                playCustomerBeep();
                if (!isOpenRef.current) {
                  setAgentArrivedBadge(true);
                  setUnreadCount((c) => c + 1);
                  if (document.hidden) {
                    const preview = event.message.message || "New message";
                    showCustomerNotification(
                      event.message.sender_name || "Agent",
                      preview.length > 80 ? preview.slice(0, 80) + "…" : preview,
                    );
                  }
                }
                if (isOpenRef.current) {
                  const text = event.message.message || "New message from agent";
                  setAgentAlert((prev) => ({
                    agentName: event.message.sender_name || prev?.agentName || "Agent",
                    agentAvatar: prev?.agentAvatar || null,
                    preview: text.length > 90 ? text.slice(0, 90) + "…" : text,
                    unread: (prev?.unread || 0) + 1,
                  }));
                }
              }
            }
            break;
          }
          case "TYPING_START":
            if (event.sender_type === "agent") setAgentTyping(true);
            break;
          case "TYPING_STOP":
            if (event.sender_type === "agent") setAgentTyping(false);
            break;
          case "AGENT_LEFT_SOFT": {
            // Agent ended their side on a Read-later (or untouched) session.
            // Don't tear anything down — the user still owns this chat.
            // Record the timestamp on both the live state and the Home
            // card, so wherever the user lands the UI shows the
            // single-CTA Read-message style.
            const ts = event.agent_ended_at || new Date().toISOString();
            setLiveAgentEndedAt(ts);
            setAgentReachOut((prev) => prev ? { ...prev, agent_ended_at: ts } : prev);
            break;
          }
          case "SESSION_REQUEUED":
            // The user replied to a soft-closed chat — backend has flipped
            // the session to WAITING for the next available agent. Drop
            // soft-close affordances and let the live view render the
            // inline "Connecting you to an agent…" indicator on top of
            // the existing transcript (see waiting branch below).
            setLivePhase("waiting");
            setLiveAgentEndedAt(null);
            setSoftCloseMode(null);
            setSoftCloseAsyncInput("");
            break;
          case "CHAT_ENDED":
            localStorage.removeItem("khub_live_session_id");
            setLivePhase("ended");
            // Stash the closing id so the Reopen button still has a target
            // after we null liveSessionId below.
            lastClosedSessionIdRef.current = liveSessionId;
            // Drop the session reference so a future reopen of the same
            // session id is treated as a fresh mount — the WS effect is
            // keyed on liveSessionId and won't re-run on a same-value set.
            setLiveSessionId(null);
            // Banner is meaningless once the session is closed.
            setAgentReachOut(null);
            setLiveAgentEndedAt(null);
            setSoftCloseMode(null);
            setSoftCloseAsyncInput("");
            // Close immediately so backend doesn't also emit USER_DISCONNECTED.
            ws.close();
            teardownScreenShare();
            setScreenShareStatus("idle");
            setScreenShareAgentName(null);
            setScreenShareMode("screen");
            appendLiveSystem(event.reason === "timeout_inactivity"
              ? "Chat closed due to 7 minutes of inactivity."
              : "Chat ended.");
            break;
          case "CHAT_TRANSFERRED": {
            const sysMsg = event.system_message;
            const newAgentName = event.new_agent?.name || "another agent";
            if (sysMsg) {
              setLiveMessages((prev) =>
                prev.some((m) => m.id === sysMsg.id) ? prev : [...prev, sysMsg]
              );
            } else {
              appendLiveSystem(`Chat has been transferred to ${newAgentName}.`);
            }
            // Tear down any active media session — the new agent will need to
            // request it again rather than inheriting the prior peer connection.
            teardownScreenShare();
            setScreenShareStatus("idle");
            setScreenShareAgentName(null);
            setScreenShareMode("screen");
            break;
          }

          // ── Media session (WebRTC signaling) ──────────────────────────────
          case "SCREEN_SHARE_REQUEST": {
            // Bring the live view forward so the consent modal is visible even
            // if the user is on the home tab when the agent asks. The agent
            // picks one of three modes (screen / voice / both); we mirror it
            // so the modal copy and accept-flow can branch correctly.
            const requestedMode = event.mode === "voice" || event.mode === "both"
              ? event.mode
              : "screen";
            setScreenShareError(null);
            setScreenShareMode(requestedMode);
            setScreenShareAgentName(event.agent?.name || "the agent");
            setScreenShareStatus("awaitingConsent");
            break;
          }
          case "SCREEN_SHARE_ANSWER":
            if (screenSharePcRef.current && event.sdp) {
              screenSharePcRef.current
                .setRemoteDescription({ type: "answer", sdp: event.sdp })
                .catch((err) => {
                  setScreenShareError(err?.message || "Could not establish stream");
                  setScreenShareStatus("error");
                  teardownScreenShare();
                });
            }
            break;
          case "SCREEN_SHARE_ICE":
            if (screenSharePcRef.current && event.candidate) {
              screenSharePcRef.current.addIceCandidate(event.candidate).catch(() => { /* late candidate */ });
            }
            break;
          case "SCREEN_SHARE_END":
            teardownScreenShare();
            setScreenShareStatus("ended");
            setScreenShareAgentName(null);
            setScreenShareMode("screen");
            break;

          // ── Remote control lifecycle (co-browsing) ───────────────────────
          case "REMOTE_CONTROL_REQUEST":
            // Agent is asking for control. Show the consent prompt — the
            // executor + data channel are set up by the screen-share flow,
            // so we just need a yes/no from the user.
            if (remoteControlChannelRef.current) {
              setRemoteControlStatus("requesting");
            }
            break;
          case "REMOTE_CONTROL_REVOKE":
            // Agent voluntarily ended (or backend forced an audit revoke).
            remoteControlActiveRef.current = false;
            setRemoteControlStatus("idle");
            setRemoteCursor(null);
            break;
          default: break;
        }
      } catch { /* ignore */ }
    };
    return () => ws.close();
  }, [liveSessionId, user, authToken]);

  // ── User-level WebSocket (for agent-initiated chats) ─────────────────────
  // Subscribes to push events that are not tied to an existing session — when
  // a CS agent proactively opens a chat with this user, we pick it up here
  // and flip the widget into live view even if it was never opened before.
  useEffect(() => {
    const token = getAuthToken(user, authToken);
    if (!token) return;
    const ws = new WebSocket(`${CS_WS_URL}/cs-api/ws/live-chat/user?token=${token}`);

    ws.onmessage = async (evt) => {
      try {
        const event = JSON.parse(evt.data);
        if (event.event !== "AGENT_INITIATED_CHAT") return;

        const newSessionId = event.session_id;
        if (!newSessionId) return;
        // Don't steal the user away from a live chat they're already in.
        // Use refs to get the latest phase/sessionId — the WS callback closure
        // would otherwise hold stale values from the render when the WS was created.
        const curPhase = livePhaseRef.current;
        const curSessionId = liveSessionIdRef.current;
        if (curPhase === "active" || curPhase === "waiting") {
          if (curSessionId === newSessionId) return;
          return;
        }

        const ack = event.user_acknowledgement || "pending";
        // Both pending and ignored mean "user hasn't read it yet" — Home owns
        // the routing. Only "accepted" should jump straight to live view.
        const isUnread = ack === "pending" || ack === "ignored";

        try {
          const [sr, mr] = await Promise.all([
            fetch(`${CS_PORTAL_URL}/cs-api/live-chat/sessions/${newSessionId}`, {
              headers: authHeaders(user, authToken),
            }),
            fetch(`${CS_PORTAL_URL}/cs-api/live-chat/sessions/${newSessionId}/messages`, {
              headers: authHeaders(user, authToken),
            }),
          ]);
          if (!sr.ok) return;
          const sess = await sr.json();
          if (sess.status !== "active" && sess.status !== "waiting") return;
          if (mr.ok) setLiveMessages(await mr.json());
          setLiveSessionId(newSessionId);
          setLivePhase("active");
          setLiveAgentEndedAt(sess.agent_ended_at || null);
          localStorage.setItem("khub_live_session_id", String(newSessionId));

          updateLiveAgent(event.agent || sess.assigned_agent);
          const agentName = event.agent?.name || sess.assigned_agent?.name || "An agent";
          const agentAvatar = resolveMediaUrl(
            event.agent?.avatar_url || sess.assigned_agent?.avatar_url
          ) || null;
          const preview = event.initial_message
            ? (event.initial_message.length > 90
              ? event.initial_message.slice(0, 90) + "…"
              : event.initial_message)
            : `${agentName} ${event.reopened ? "reopened your chat" : "reached out"} — they're waiting for you.`;

          if (isUnread) {
            // Don't hijack the user into live view. Surface the card on Home —
            // pending shows Accept / Read-later; ignored shows the persistent
            // Read-later card with a single Read-message CTA. If the agent
            // has already stepped away (soft-closed), the card shows the
            // Read-message style regardless of pending/ignored.
            setAgentReachOut({
              sessionId: newSessionId,
              agentName,
              agentAvatar,
              preview,
              reopened: !!event.reopened,
              acknowledged: ack === "ignored" ? "ignored" : undefined,
              agent_ended_at: sess.agent_ended_at || null,
            });
            if (!isOpenRef.current) {
              setAgentArrivedBadge(true);
              setUnreadCount((c) => c + 1);
              playCustomerBeep();
              if (document.hidden) showCustomerNotification("New message", `${agentName} sent you a message`);
            } else if (viewRef.current !== "tabs") {
              // Open widget but on chat/live — keep them where they are but
              // pulse the home tab indirectly via the unread chip.
              setActiveTab("home");
              setView("tabs");
            }
          } else {
            // Already accepted elsewhere (e.g. another tab) — restore to live.
            setView("live");
            if (!isOpenRef.current) {
              setAgentArrivedBadge(true);
              setUnreadCount((c) => c + 1);
              playCustomerBeep();
              if (document.hidden) showCustomerNotification("New message", `${agentName} sent you a message`);
            } else if (viewRef.current !== "live") {
              setAgentAlert({ agentName, agentAvatar, preview, unread: 1 });
            }
          }
        } catch { /* ignore */ }
      } catch { /* ignore */ }
    };

    return () => ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authToken]);

  // ── Poll queue position ────────────────────────────────────────────────────
  useEffect(() => {
    if (livePhase !== "waiting" || !liveSessionId) return;
    const poll = async () => {
      try {
        const r = await fetch(
          `${CS_PORTAL_URL}/cs-api/live-chat/sessions/${liveSessionId}/queue-position`,
          { headers: authHeaders(user, authToken) }
        );
        if (r.ok) setQueueInfo(await r.json());
      } catch { /* ignore */ }
    };
    poll();
    queuePollRef.current = setInterval(poll, 5000);
    return () => clearInterval(queuePollRef.current);
  }, [livePhase, liveSessionId, user, authToken]);

  // ── Consent handler ───────────────────────────────────────────────────────
  const handleConsent = useCallback(async () => {
    try {
      await fetch(`${CS_PORTAL_URL}/cs-api/chatbot/privacy-consent`, {
        method: "POST", headers: authHeaders(user, authToken),
      });
    } catch { /* best effort */ }
    localStorage.setItem("khub_chat_consent", "1");
    setConsentDone(true);
    setShowConsent(false);
  }, [user, authToken]);

  // ── Open widget ───────────────────────────────────────────────────────────
  const handleOpen = async () => {
    setIsOpen(true);
    setMinimized(false);
    setAgentArrivedBadge(false);
    setUnreadCount(0);
    setNewsNag(false);
    setNewsAlert(null);
    if (!consentDone) { setShowConsent(true); return; }
    // If an agent reached out and the user hasn't actually read the message
    // yet (pending or deferred via Read-later), land on Home so the card is
    // the first thing they see — don't jump straight into the live chat even
    // though livePhase is "active".
    if (agentReachOut) {
      setView("tabs");
      setActiveTab("home");
      return;
    }
    // If there's a live chat already in flight, jump straight to it —
    // especially important when the launcher badge fired because the
    // agent joined / replied while the widget was closed. Otherwise the
    // user lands on Home and can't find the agent's messages.
    if (livePhase === "active" || livePhase === "waiting") {
      setView("live");
      setAgentAlert(null);
      return;
    }
    // Preserve in-progress async capture so the user can continue
    if (livePhase === "async") {
      setView("live");
      setAgentAlert(null);
      return;
    }
    // Reset ended live chat to idle
    if (livePhase === "ended") {
      setLivePhase("idle");
      setLiveMessages([]);
      setBotHistory([]);
      setLiveSessionId(null);
      localStorage.removeItem("khub_live_session_id");
      setView("tabs");
      return;
    }
    // Reset stale bot chat state when no active session
    if (!session || chatEnded || sessionClosed) {
      setView("tabs");
      setMessages([]);
      setChatEnded(false);
      setSessionClosed(false);
      setEscalated(false);
      setShowEscalateBar(false);
      setInput("");
      return;
    }
    // Session exists in memory and looks active — verify against the server
    // before showing the chat. The chatbot inactivity monitor flips
    // abandoned sessions to AUTO_CLOSED after CHATBOT_INACTIVITY_MINUTES,
    // and without this check the user would see their old transcript and
    // hit a 4xx on the next send.
    try {
      const r = await fetch(`${CS_PORTAL_URL}/cs-api/chatbot/sessions/${session.id}`, {
        headers: authHeaders(user, authToken),
      });
      if (!r.ok) return; // leave state intact — likely transient (auth/network)
      const fresh = await r.json();
      if (fresh.status === "active" || fresh.status === "escalated") {
        setSession(fresh);
        return;
      }
      // Server-side closed (auto_closed / closed) — drop everything and
      // land on home so the next interaction starts a fresh session.
      localStorage.removeItem("khub_bot_session_id");
      setSession(null);
      setMessages([]);
      setChatEnded(false);
      setSessionClosed(false);
      setEscalated(false);
      setShowEscalateBar(false);
      setInput("");
      setView("tabs");
      setActiveTab("home");
    } catch {
      // Network blip — keep the stale session in memory rather than wiping
      // the user's conversation. A real send failure will surface later.
    }
  };

  // ── Minimize widget ─────────────────────────────────────────────────────────
  // Hide the panel but keep a small bubble docked at the bottom. Unlike the X
  // (handleCloseWidget), this performs NO teardown — clicking the bubble pops
  // the exact same conversation back open.
  const handleMinimize = useCallback(() => {
    setIsOpen(false);
    setMinimized(true);
    setShowEmoji(false);
  }, []);

  // Docked bubble / preview-card click. When the bubble is hopping for a
  // broadcast, land on K-News (overriding handleOpen's usual home/live
  // routing); "Read now" additionally opens that broadcast in the reader.
  const handleLauncherClick = async (bc) => {
    const fromNews = newsNag || !!bc;
    await handleOpen();
    if (!fromNews) return;
    setView("tabs");
    setActiveTab("news");
    if (bc) openBroadcast(bc);
  };

  // ── Chatbot session ───────────────────────────────────────────────────────
  const closeCurrentSession = useCallback(async (sess) => {
    if (!sess || sess.status === "closed" || sess.status === "auto_closed") return;
    try {
      await fetch(`${CS_PORTAL_URL}/cs-api/chatbot/sessions/${sess.id}/close`, {
        method: "POST", headers: authHeaders(user, authToken),
      });
    } catch { /* best-effort */ }
  }, [user, authToken]);

  const startSession = useCallback(async () => {
    if (session) return session;
    try {
      const res = await fetch(`${CS_PORTAL_URL}/cs-api/chatbot/sessions`, {
        method: "POST", headers: authHeaders(user, authToken),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSession(data);
      return data;
    } catch {
      setError("Could not start chat session. Please try again.");
      return null;
    }
  }, [session, user, authToken]);

  const escalate = useCallback(async () => {
    if (!session || escalated) return;
    try {
      const res = await fetch(
        `${CS_PORTAL_URL}/cs-api/chatbot/sessions/${session.id}/escalate`,
        { method: "POST", headers: authHeaders(user, authToken) }
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setEscalated(true);
      setMessages((prev) => [
        ...prev,
        { role: "system", content: data.message || "A support agent will be with you shortly.", id: `sys-${Date.now()}` },
      ]);
    } catch { setError("Could not escalate. Please try again."); }
  }, [session, escalated, user, authToken]);

  const handleEndBotChat = useCallback(async () => {
    setConfirmEnd(false);
    setChatEnded(true);
    setShowEscalateBar(false);
    localStorage.removeItem("khub_bot_session_id");
    await closeCurrentSession(session);
    setSession(null);
    setMessages((prev) => [...prev, {
      role: "system",
      content: "Chat ended. Thanks for using Khan Bot Support.",
      id: `sys-end-${Date.now()}`,
    }]);
  }, [session, closeCurrentSession]);

  const handleCloseWidget = useCallback(() => {
    // X is "minimise", not "end". Anything mid-flight — bot chat OR live chat —
    // must survive so reopening the bubble drops the user back where they were.
    // Explicit end paths (`handleEndBotChat`, `endLiveChat`) own actual tear-down.
    // Abandoned bot sessions are reaped server-side by the chatbot inactivity
    // monitor after CHATBOT_INACTIVITY_MINUTES of silence.
    const hasInFlightBotChat =
      !!session &&
      !chatEnded &&
      !sessionClosed &&
      (session.status === "active" || session.status === "escalated");
    const hasInFlightLiveChat =
      livePhase === "active" ||
      livePhase === "waiting" ||
      (livePhase === "async" && asyncStep === "submitted");

    setIsOpen(false);
    setMinimized(false);
    setShowEmoji(false);
    setConfirmEnd(false);

    // Only reset bot chat state when there's nothing worth resuming.
    if (!hasInFlightBotChat) {
      setSession(null);
      setMessages([]);
      setChatEnded(false);
      setSessionClosed(false);
      setEscalated(false);
      setShowEscalateBar(false);
      setInput("");
    }

    if (!hasInFlightBotChat && !hasInFlightLiveChat) {
      setView("tabs");
      setConfirmLiveEnd(false);
      if (livePhase === "ended") {
        setLivePhase("idle");
        setLiveMessages([]);
        setBotHistory([]);
        setLiveSessionId(null);
        localStorage.removeItem("khub_live_session_id");
      }
      if (livePhase === "async") {
        // User closed before submitting — discard partial capture
        setLivePhase("idle");
        setAsyncStep(null);
        setAsyncMessages([]);
        setAsyncIssue("");
        setAsyncUrgent(null);
        setAsyncInput("");
      }
    }
  }, [session, chatEnded, sessionClosed, livePhase, asyncStep]);

  // ── Topbar launcher bridge ──────────────────────────────────────────────────
  // The launcher icon lives in the Metronic topbar; this widget owns the panel
  // and all its state. Keep a ref to the latest open/close logic so we can
  // register a single stable toggle handler (no re-register every render), and
  // publish a snapshot of state the topbar icon renders from.
  const launcherToggleRef = useRef(() => {});
  launcherToggleRef.current = () => {
    if (isOpenRef.current) handleCloseWidget();
    else handleOpen();
  };
  useEffect(() => {
    return registerLauncherToggle(() => launcherToggleRef.current());
  }, []);

  useEffect(() => {
    // chatCount = incoming live/agent chat messages (unreadCount); broadcastCount
    // = unseen broadcasts/K-News (unseenCount). Both accrue only while closed.
    publishLauncherState({
      available: !!config,
      isOpen,
      chatCount: unreadCount,      // incoming live/agent chat messages
      broadcastCount: unseenCount, // unseen broadcasts / K-News
      attention: !isOpen && (agentArrivedBadge || unseenCount > 0),
      brandColor: config?.brand_color || "#e91e63",
    });
  }, [config, isOpen, unreadCount, unseenCount, agentArrivedBadge]);

  // Hide the topbar icon if the widget unmounts (e.g. logout / route teardown).
  useEffect(() => {
    return () => publishLauncherState({ available: false, isOpen: false, chatCount: 0, broadcastCount: 0 });
  }, []);

  const handleStartNewChat = useCallback(async () => {
    setChatEnded(false);
    setConfirmEnd(false);
    setShowEscalateBar(false);
    localStorage.removeItem("khub_bot_session_id");
    await closeCurrentSession(session);
    setSession(null);
    setMessages([]);
    setSessionClosed(false);
    setEscalated(false);
    // Also clear any ended live chat so returning to the widget starts fresh.
    if (livePhase === "ended") {
      localStorage.removeItem("khub_live_session_id");
      setLivePhase("idle");
      setLiveMessages([]);
      setBotHistory([]);
      setLiveSessionId(null);
    }
    setView("tabs");
    setActiveTab("home");
  }, [session, livePhase, closeCurrentSession]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setError(null);
    setLoading(true);
    setShowEmoji(false);
    const optimistic = { role: "user", content: text, id: `opt-${Date.now()}` };
    setMessages((prev) => [...prev, optimistic]);
    try {
      let sess = session;
      if (!sess) sess = await startSession();
      if (!sess) { setLoading(false); return; }
      const res = await fetch(`${CS_PORTAL_URL}/cs-api/chatbot/sessions/${sess.id}/message`, {
        method: "POST", headers: authHeaders(user, authToken),
        body: JSON.stringify({ message: text }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== optimistic.id),
        data.user_message, data.bot_message,
      ]);
      if (data.bot_message?.suggested_actions?.includes("Start a new chat")) setSessionClosed(true);
      if (isEscalationTrigger(text) || data.bot_message?.suggested_actions?.includes("Talk to a human agent")) {
        setShowEscalateBar(true);
      }
    } catch {
      setError("Failed to send message. Please try again.");
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
    } finally { setLoading(false); }
  }, [input, loading, session, startSession, user, authToken]);

  // Rate a bot answer. Thumbs-up reinforces the knowledge entry that produced it;
  // thumbs-down flags it for the CS team's Knowledge page so the answer improves
  // next time. Optimistic — reverts the chip if the request fails so it can retry.
  const sendFeedback = useCallback(async (messageId, isHelpful) => {
    if (feedback[messageId]) return;
    setFeedback((prev) => ({ ...prev, [messageId]: isHelpful ? "up" : "down" }));
    try {
      const res = await fetch(
        `${CS_PORTAL_URL}/cs-api/chatbot/messages/${messageId}/feedback`,
        {
          method: "POST",
          headers: authHeaders(user, authToken),
          body: JSON.stringify({ is_helpful: isHelpful }),
        }
      );
      if (!res.ok) throw new Error();
    } catch {
      setFeedback((prev) => {
        const next = { ...prev };
        delete next[messageId];
        return next;
      });
    }
  }, [feedback, user, authToken]);

  const handleSuggestedAction = useCallback(async (action) => {
    if (action === "Talk to a human agent") {
      setView("live");
      setLivePhase("idle");
      return;
    }
    if (action === "Yes, connect me") {
      setMessages((prev) => [
        ...prev,
        { role: "user", content: "Yes, connect me", id: `opt-${Date.now()}` },
        { role: "assistant", content: "I'll connect you with a support agent right away. Please hold on.", id: `sys-${Date.now()}` },
      ]);
      setTimeout(() => { setView("live"); setLivePhase("idle"); }, 1500);
      return;
    }
    if (action === "Raise a ticket") {
      setMessages((prev) => [
        ...prev,
        { role: "user", content: "Raise a ticket", id: `opt-${Date.now()}` },
        {
          role: "assistant",
          content: "Opening the Support page so you can raise a ticket. You can track its status there anytime.\n\nOnce your ticket is raised, would you like to close this chat or is there anything else I can help you with?",
          id: `sys-${Date.now()}`,
          suggested_actions: ["Yes, close chat", "Ask something else"],
        },
      ]);
      setTimeout(() => { window.open("/support", "_blank", "noopener,noreferrer"); }, 600);
      return;
    }
    if (action === "Yes, close chat") {
      setMessages((prev) => [
        ...prev,
        { role: "user", content: "Yes, close chat", id: `opt-${Date.now()}` },
      ]);
      await handleEndBotChat();
      return;
    }
    if (action === "Ask something else") {
      setMessages((prev) => [
        ...prev,
        { role: "user", content: "Ask something else", id: `opt-${Date.now()}` },
        { role: "assistant", content: "Sure! What else can I help you with?", id: `sys-${Date.now()}` },
      ]);
      return;
    }
    if (action === "No, thanks") {
      setMessages((prev) => [
        ...prev,
        { role: "user", content: "No, thanks", id: `opt-${Date.now()}` },
        { role: "assistant", content: "No problem! Feel free to ask if you need anything else.", id: `sys-${Date.now()}` },
      ]);
      return;
    }
    if (action === "Start a new chat") {
      localStorage.removeItem("khub_bot_session_id");
      await closeCurrentSession(session);
      setSession(null); setMessages([]); setSessionClosed(false); setEscalated(false);
      if (config?.welcome_message) setMessages([{ role: "assistant", content: config.welcome_message, id: "welcome" }]);
      return;
    }
    setLoading(true);
    const optimistic = { role: "user", content: action, id: `opt-${Date.now()}` };
    setMessages((prev) => [...prev, optimistic]);
    try {
      let sess = session;
      if (!sess) sess = await startSession();
      if (!sess) { setLoading(false); return; }
      const res = await fetch(`${CS_PORTAL_URL}/cs-api/chatbot/sessions/${sess.id}/message`, {
        method: "POST", headers: authHeaders(user, authToken),
        body: JSON.stringify({ message: action }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== optimistic.id),
        data.user_message, data.bot_message,
      ]);
      if (data.bot_message?.suggested_actions?.includes("Start a new chat")) setSessionClosed(true);
      if (isEscalationTrigger(action) || data.bot_message?.suggested_actions?.includes("Talk to a human agent")) {
        setShowEscalateBar(true);
      }
    } catch { setMessages((prev) => prev.filter((m) => m.id !== optimistic.id)); }
    finally { setLoading(false); }
  }, [session, startSession, closeCurrentSession, user, authToken, escalate, config, handleEndBotChat]);

  // ── Open chatbot with optional pre-fill ───────────────────────────────────
  // Always starts a fresh session — this is only ever called from the home/
  // messages tab, meaning the user explicitly wants a new conversation.
  const openChatWith = useCallback(async (prefill = "") => {
    setChatEnded(false);
    setConfirmEnd(false);
    setEscalated(false);
    setSessionClosed(false);
    setShowEscalateBar(false);
    setMessages([]);
    localStorage.removeItem("khub_bot_session_id");
    setSession(null);
    setView("chat");
    setInput(prefill);
    try {
      const res = await fetch(`${CS_PORTAL_URL}/cs-api/chatbot/sessions`, {
        method: "POST", headers: authHeaders(user, authToken),
      });
      if (!res.ok) throw new Error();
      const newSess = await res.json();
      setSession(newSess);
      if (config?.welcome_message) {
        setMessages([{ role: "assistant", content: config.welcome_message, id: "welcome" }]);
      }
    } catch {
      setError("Could not start chat session. Please try again.");
    }
  }, [user, authToken, config]);

  // ── FAQ click ──────────────────────────────────────────────────────────────
  const handleFaqClick = useCallback(async (q) => {
    try {
      await fetch(`${CS_PORTAL_URL}/cs-api/chatbot/faq-questions/${q.id}/track`, {
        method: "POST", headers: authHeaders(user, authToken),
      });
    } catch { /* ignore */ }
    if (q.category === "support") {
      setView("live");
      setLivePhase("idle");
    } else {
      await openChatWith(q.question);
    }
  }, [user, authToken, openChatWith]);

  // ── Live chat ──────────────────────────────────────────────────────────────
  const appendLiveSystem = (text) => {
    setLiveMessages((prev) => [...prev, {
      id: Date.now(), sender_type: "system", sender_name: "System",
      message: text, message_type: "text", created_at: new Date().toISOString(),
    }]);
  };

  // Pushes a bot "system" message into the async capture flow transcript
  const pushAsyncBot = useCallback((content, actions) => {
    setAsyncMessages((prev) => [
      ...prev,
      { role: "assistant", content, id: `abot-${Date.now()}-${Math.random()}`, actions: actions || null },
    ]);
  }, []);

  const pushAsyncUser = useCallback((content) => {
    setAsyncMessages((prev) => [
      ...prev,
      { role: "user", content, id: `auser-${Date.now()}-${Math.random()}` },
    ]);
  }, []);

  const startAsyncFlow = useCallback(() => {
    setLivePhase("async");
    setAsyncStep("askIssue");
    setAsyncMessages([]);
    setAsyncIssue("");
    setAsyncUrgent(null);
    setAsyncInput("");
    setLiveError(null);
    // Slight sequencing so messages feel conversational
    setTimeout(() => {
      pushAsyncBot("Hi 👋 Looks like our agents are offline right now. I can take your request and have someone follow up.");
    }, 50);
    setTimeout(() => {
      pushAsyncBot("Can you briefly describe your issue?");
    }, 550);
  }, [pushAsyncBot]);

  const submitAsyncRequest = useCallback(async (issue, urgent) => {
    setAsyncStep("submitting");
    setLiveError(null);
    const priority = urgent ? "high" : "normal";
    try {
      const res = await fetch(`${CS_PORTAL_URL}/cs-api/live-chat/request`, {
        method: "POST",
        headers: authHeaders(user, authToken),
        body: JSON.stringify({
          chatbot_session_id: session?.id || null,
          context_summary: `Async request — urgent: ${urgent ? "yes" : "no"}`,
          priority,
          is_async: true,
          initial_message: issue,
        }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setLiveSessionId(data.id);
      setAsyncStep("submitted");
      const sla = urgent ? "within 1 hour" : "within 4 business hours";
      pushAsyncBot(
        `Thanks! Our team will review your request and get back to you ${sla}. ` +
        `You can close this window — we'll notify you here as soon as an agent picks it up.`
      );
    } catch {
      setAsyncStep("askPriority");
      setLiveError("Something went wrong submitting your request. Please try again.");
    }
  }, [user, authToken, session, pushAsyncBot]);

  const requestAgent = useCallback(async () => {
    setLiveError(null);
    // Pre-check availability so we can branch to async flow when no agents are online
    try {
      const availRes = await fetch(`${CS_PORTAL_URL}/cs-api/live-chat/availability`, {
        headers: authHeaders(user, authToken),
      });
      if (availRes.ok) {
        const avail = await availRes.json();
        if (!avail.has_online_agents) {
          startAsyncFlow();
          return;
        }
      }
    } catch { /* fall through to live request */ }

    setLivePhase("waiting");
    setLiveMessages([]);
    setBotHistory([]);
    setLiveSessionId(null);
    try {
      const res = await fetch(`${CS_PORTAL_URL}/cs-api/live-chat/request`, {
        method: "POST", headers: authHeaders(user, authToken),
        body: JSON.stringify({
          chatbot_session_id: session?.id || null,
          context_summary: null,
          priority: "normal",
        }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setLiveSessionId(data.id);
    } catch (err) {
      setLivePhase("idle");
      setLiveError("Failed to connect. Please try again.");
    }
  }, [user, authToken, session, startAsyncFlow]);

  // ── Async flow step handlers ──────────────────────────────────────────────
  const submitAsyncIssue = useCallback(() => {
    const text = asyncInput.trim();
    if (!text) return;
    pushAsyncUser(text);
    setAsyncIssue(text);
    setAsyncInput("");
    setAsyncStep("askPriority");
    setTimeout(() => {
      pushAsyncBot("Is this urgent?", ["Yes (High Priority)", "No (Normal)"]);
    }, 300);
  }, [asyncInput, pushAsyncBot, pushAsyncUser]);

  const pickAsyncPriority = useCallback((urgent) => {
    pushAsyncUser(urgent ? "Yes (High Priority)" : "No (Normal)");
    setAsyncUrgent(urgent);
    setAsyncStep("submitting");
    submitAsyncRequest(asyncIssue, urgent);
  }, [asyncIssue, pushAsyncUser, submitAsyncRequest]);

  const handleAsyncInputSubmit = useCallback(() => {
    if (asyncStep === "askIssue") submitAsyncIssue();
  }, [asyncStep, submitAsyncIssue]);

  const sendLiveMessage = useCallback(async () => {
    const text = liveInput.trim();
    // Allow sends in active OR waiting (waiting covers the re-queued state
    // after a soft-closed chat — the user keeps the composer until pickup).
    if (!text || (livePhase !== "active" && livePhase !== "waiting")) return;
    setLiveInput("");
    setShowEmoji(false);
    sendTypingStop();
    try {
      const res = await fetch(
        `${CS_PORTAL_URL}/cs-api/live-chat/sessions/${liveSessionId}/messages`,
        {
          method: "POST", headers: authHeaders(user, authToken),
          body: JSON.stringify({ message: text }),
        }
      );
      if (!res.ok) throw new Error();
      // Track the first unanswered turn while the chat is live so the
      // long-wait prompt can offer the offline fallback after 2 min of
      // agent silence (covers the outbound reach-out case where the
      // agent has stopped responding). Cleared on agent reply via WS.
      if (livePhase === "active") {
        setUserMsgPendingAt((prev) => prev ?? Date.now());
      }
    } catch { setLiveError("Failed to send message."); }
  }, [liveInput, livePhase, liveSessionId, user, authToken]);

  // ── Soft-close (Read-later) reply path ─────────────────────────────────────
  // User reads a chat the agent has stepped away from. We hide the normal
  // input until they explicitly tap "Chat now" — then we branch on agent
  // availability:
  //   live  → agents online, give them the regular composer back
  //   async → no agents online, single textarea + "Send and close"
  const softCloseStartChat = useCallback(async () => {
    setLiveError(null);
    try {
      const r = await fetch(`${CS_PORTAL_URL}/cs-api/live-chat/availability`, {
        headers: authHeaders(user, authToken),
      });
      if (r.ok) {
        const avail = await r.json();
        setSoftCloseMode(avail.has_online_agents ? "live" : "async");
        return;
      }
    } catch { /* fall through */ }
    // Availability check failed — assume offline so the user always has a
    // way to leave a final message and close.
    setSoftCloseMode("async");
  }, [user, authToken]);

  const softCloseSendAndClose = useCallback(async () => {
    const text = softCloseAsyncInput.trim();
    if (!text || !liveSessionId || softCloseSubmitting) return;
    setSoftCloseSubmitting(true);
    setLiveError(null);
    try {
      // Send the user's parting message first…
      const sendRes = await fetch(
        `${CS_PORTAL_URL}/cs-api/live-chat/sessions/${liveSessionId}/messages`,
        {
          method: "POST", headers: authHeaders(user, authToken),
          body: JSON.stringify({ message: text }),
        }
      );
      if (!sendRes.ok) throw new Error();
      // …then close the session so it lands in Recent.
      await fetch(`${CS_PORTAL_URL}/cs-api/live-chat/sessions/${liveSessionId}/end`, {
        method: "POST", headers: authHeaders(user, authToken),
        body: JSON.stringify({ reason: "user_ended" }),
      });
      localStorage.removeItem("khub_live_session_id");
      liveWsRef.current?.close();
      setSoftCloseAsyncInput("");
      setSoftCloseMode(null);
      lastClosedSessionIdRef.current = liveSessionId;
      setLivePhase("ended");
    } catch {
      setLiveError("Could not send your message. Please try again.");
    } finally {
      setSoftCloseSubmitting(false);
    }
  }, [softCloseAsyncInput, liveSessionId, softCloseSubmitting, user, authToken]);

  const endLiveChat = useCallback(async () => {
    localStorage.removeItem("khub_live_session_id");
    if (!liveSessionId) { setView("tabs"); return; }
    try {
      await fetch(`${CS_PORTAL_URL}/cs-api/live-chat/sessions/${liveSessionId}/end`, {
        method: "POST", headers: authHeaders(user, authToken),
        body: JSON.stringify({ reason: "user_ended" }),
      });
    } catch { /* best effort */ }
    // Close WebSocket immediately after the /end call so the server's
    // WebSocketDisconnect handler doesn't emit a redundant USER_DISCONNECTED.
    liveWsRef.current?.close();
    lastClosedSessionIdRef.current = liveSessionId;
    setLivePhase("ended");
  }, [liveSessionId, user, authToken]);

  // Customer chose "Leave a message" from the long-wait prompt — either
  // while queued, or while in an active chat the agent has gone silent on
  // (e.g. outbound reach-out where the agent stopped responding). End the
  // current live session in the background and switch the widget into the
  // existing offline (async) flow so the agent picks it up like any other
  // async request — no new backend path, no email, no new statuses.
  const leaveMessageAndStartOffline = useCallback(async () => {
    setShowLongWaitPrompt(false);
    setUserMsgPendingAt(null);
    const sessionToCancel = liveSessionId;
    setLiveSessionId(null);
    localStorage.removeItem("khub_live_session_id");
    liveWsRef.current?.close();
    startAsyncFlow();
    if (sessionToCancel) {
      try {
        await fetch(`${CS_PORTAL_URL}/cs-api/live-chat/sessions/${sessionToCancel}/end`, {
          method: "POST", headers: authHeaders(user, authToken),
          body: JSON.stringify({ reason: "user_ended" }),
        });
      } catch { /* best effort */ }
    }
  }, [liveSessionId, user, authToken, startAsyncFlow]);

  // Arm a one-shot 2-minute timer for the long-wait prompt:
  //   • waiting → fires 2 min after entering the queue
  //   • active  → fires 2 min after the user's first unanswered message
  //               (cleared by agent reply via WS NEW_MESSAGE)
  // Any other phase (idle/async/ended) clears the prompt and pending flag.
  useEffect(() => {
    if (livePhase !== "waiting" && livePhase !== "active") {
      setShowLongWaitPrompt(false);
      setUserMsgPendingAt(null);
      return;
    }
    if (livePhase === "waiting") {
      const t = setTimeout(() => setShowLongWaitPrompt(true), 120000);
      return () => clearTimeout(t);
    }
    if (!userMsgPendingAt) return;
    const remaining = Math.max(0, 120000 - (Date.now() - userMsgPendingAt));
    const t = setTimeout(() => setShowLongWaitPrompt(true), remaining);
    return () => clearTimeout(t);
  }, [livePhase, userMsgPendingAt]);

  // Reopen a closed session by spawning a NEW live-chat session linked to it.
  // The user keeps the original transcript and the new session enters the
  // queue so any available agent can pick it up via the normal flow.
  const reopenLiveChat = useCallback(async () => {
    // After CHAT_ENDED nulls liveSessionId we fall back to the id captured
    // when the chat closed, so the Reopen button still has a target.
    const targetSessionId = liveSessionId || lastClosedSessionIdRef.current;
    if (!targetSessionId) return;
    setLiveError("");
    try {
      const r = await fetch(
        `${CS_PORTAL_URL}/cs-api/live-chat/sessions/${targetSessionId}/reopen`,
        { method: "POST", headers: authHeaders(user, authToken) },
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        setLiveError(err.detail || "Could not reopen the chat. Please try again.");
        return;
      }
      const fresh = await r.json();
      // Close the old WS — the effect will reconnect to the new session id.
      liveWsRef.current?.close();
      setBotHistory([]);
      setChatEnded(false);
      lastClosedSessionIdRef.current = null;
      setLiveSessionId(fresh.id);
      localStorage.setItem("khub_live_session_id", String(fresh.id));
      setLivePhase("waiting");
      // Pull the full transcript for the new session — the backend walks the
      // reopen chain so we get the original messages plus the new system
      // banner. Without this, the user would stare at an empty queue view
      // until the agent accepts and the WS triggers a refetch.
      try {
        const [mr, br] = await Promise.all([
          fetch(`${CS_PORTAL_URL}/cs-api/live-chat/sessions/${fresh.id}/messages`, {
            headers: authHeaders(user, authToken),
          }),
          fetch(`${CS_PORTAL_URL}/cs-api/live-chat/sessions/${fresh.id}/bot-history`, {
            headers: authHeaders(user, authToken),
          }).catch(() => null),
        ]);
        if (mr.ok) setLiveMessages(await mr.json());
        else setLiveMessages([]);
        if (br && br.ok) setBotHistory(await br.json());
      } catch {
        setLiveMessages([]);
      }
    } catch {
      setLiveError("Could not reopen the chat. Please try again.");
    }
  }, [liveSessionId, user, authToken]);

  // ── Typing indicators ──────────────────────────────────────────────────────
  const sendTypingStart = useCallback(() => {
    if (!liveWsRef.current || liveWsRef.current.readyState !== 1) return;
    liveWsRef.current.send(JSON.stringify({ event: "TYPING_START", sender_type: "user" }));
  }, []);

  const sendTypingStop = useCallback(() => {
    if (!liveWsRef.current || liveWsRef.current.readyState !== 1) return;
    liveWsRef.current.send(JSON.stringify({ event: "TYPING_STOP", sender_type: "user" }));
  }, []);

  const handleLiveInputChange = (e) => {
    setLiveInput(e.target.value);
    sendTypingStart();
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(sendTypingStop, TYPING_DEBOUNCE_MS);
  };

  // ── File upload (live chat only) ───────────────────────────────────────────
  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !liveSessionId || (livePhase !== "active" && livePhase !== "waiting")) return;
    e.target.value = "";
    setUploading(true);
    setLiveError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(
        `${CS_PORTAL_URL}/cs-api/live-chat/sessions/${liveSessionId}/messages/upload`,
        { method: "POST", headers: authHeadersForm(user, authToken), body: form }
      );
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
    } catch (err) {
      setLiveError(err.message || "File upload failed.");
    } finally { setUploading(false); }
  }, [liveSessionId, livePhase, user, authToken]);

  // ── Emoji insert ───────────────────────────────────────────────────────────
  const insertEmoji = (emoji) => {
    if (view === "live") setLiveInput((p) => p + emoji);
    else setInput((p) => p + emoji);
    setShowEmoji(false);
  };

  // ── Keyboard handlers ──────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };
  const handleLiveKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendLiveMessage(); }
  };

  const hasActiveMediaSession = screenShareStatus === "live" || screenShareStatus === "connecting";

  useEffect(() => {
    if (!screenShareRemoteStream) {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
        remoteAudioRef.current.remove();
        remoteAudioRef.current = null;
      }
      return;
    }
    if (!remoteAudioRef.current) {
      const el = document.createElement("audio");
      el.autoplay = true;
      el.playsInline = true;
      el.style.display = "none";
      document.body.appendChild(el);
      remoteAudioRef.current = el;
    }
    remoteAudioRef.current.srcObject = screenShareRemoteStream;
    remoteAudioRef.current.play().catch(() => {});
    return () => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
        remoteAudioRef.current.remove();
        remoteAudioRef.current = null;
      }
    };
  }, [screenShareRemoteStream]);

  if (!config) return null;
  const brandColor = config.brand_color || "#e91e63";
  const questions = faqQuestions.length > 0 ? faqQuestions : DEFAULT_FAQ;

  return (
    <>
      {/* ── Chat panel ──────────────────────────────────────────────────── */}
      {(isOpen || hasActiveMediaSession) && (
        <div className={`khub-panel${!isOpen && hasActiveMediaSession ? " khub-panel--hidden" : ""}`}>
          {/* Agent-is-here toast — appears when agent activity happens while
              the user is on a non-live view. Jumps to the live chat on click. */}
          {agentAlert && view !== "live" && (
            <button
              type="button"
              className="khub-agent-alert"
              style={{ borderColor: brandColor }}
              onClick={() => {
                setAgentAlert(null);
                setView("live");
              }}
            >
              <span
                className="khub-agent-alert-avatar"
                style={{
                  background: agentAlert.agentAvatar ? "transparent" : brandColor,
                  overflow: "hidden",
                  padding: 0,
                }}
              >
                {agentAlert.agentAvatar ? (
                  <img
                    src={agentAlert.agentAvatar}
                    alt={agentAlert.agentName || "Agent"}
                    style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit", display: "block" }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  (agentAlert.agentName || "A").charAt(0).toUpperCase()
                )}
              </span>
              <span className="khub-agent-alert-body">
                <span className="khub-agent-alert-title">
                  {agentAlert.agentName}
                  {agentAlert.unread > 0 && (
                    <span className="khub-agent-alert-unread" style={{ background: brandColor }}>
                      {agentAlert.unread}
                    </span>
                  )}
                </span>
                <span className="khub-agent-alert-preview">{agentAlert.preview}</span>
              </span>
            </button>
          )}

          {/* Privacy consent overlay */}
          {showConsent && (
            <div className="khub-consent-overlay">
              <div className="khub-consent-logo" style={{ background: brandColor }}>K</div>
              <h3>Welcome to KHub Support</h3>
              <p>
                By starting a chat, you agree to our{" "}
                <a
                  href={`${CS_PORTAL_URL}/cs-api/chatbot/privacy-policy/page`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: brandColor }}
                >
                  Privacy Policy
                </a>
                . We may collect your messages and files to provide support.
              </p>
              <button
                className="khub-consent-btn"
                style={{ background: brandColor }}
                onClick={handleConsent}
              >
                Start Chatting
              </button>
              <button className="khub-consent-cancel" onClick={() => setIsOpen(false)}>
                Cancel
              </button>
            </div>
          )}

          {/* ── Media-session consent prompt (agent → user request) ─────── */}
          {screenShareStatus === "awaitingConsent" && (() => {
            // Mode-aware copy. The agent has already picked one of three
            // intents on their side; we just render the matching wording.
            const modalConfig = screenShareMode === "voice"
              ? {
                  title: "Start a voice call?",
                  body: (
                    <>
                      <strong>{screenShareAgentName || "The agent"}</strong> wants to talk to you over voice.
                      Your browser will ask for microphone access. Audio is only sent to your support agent.
                    </>
                  ),
                  allowLabel: "Allow microphone",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <line x1="12" y1="19" x2="12" y2="23" />
                    </svg>
                  ),
                }
              : screenShareMode === "both"
              ? {
                  title: "Voice call + screen share?",
                  body: (
                    <>
                      <strong>{screenShareAgentName || "The agent"}</strong> wants to talk to you and view your screen.
                      Your browser will ask for microphone access, then for which screen to share — pick{" "}
                      <strong>Entire Screen</strong> so the agent can follow you across tabs and apps.
                      Either stops when you end the session.
                    </>
                  ),
                  allowLabel: "Allow mic + screen",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <path d="M9 21h6" />
                      <path d="M16 7v4a2 2 0 0 1-4 0V7" />
                    </svg>
                  ),
                }
              : {
                  title: "Share your screen?",
                  body: (
                    <>
                      <strong>{screenShareAgentName || "The agent"}</strong> is asking to view your screen.
                      Choose <strong>Entire Screen</strong> so they can follow you across tabs and apps —
                      picking a single tab means they see nothing once you switch away from it.
                      They'll only see what you choose to share, and you can stop sharing at any time.
                    </>
                  ),
                  allowLabel: "Allow",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                  ),
                };
            return (
              <div
                className="khub-consent-overlay"
                style={{ zIndex: 60 }}
                onClick={() => declineScreenShare("user_declined")}
              >
                <div
                  className="khub-consent-logo"
                  style={{ background: brandColor }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {modalConfig.icon}
                </div>
                <h3 onClick={(e) => e.stopPropagation()}>{modalConfig.title}</h3>
                <p onClick={(e) => e.stopPropagation()}>{modalConfig.body}</p>
                <button
                  className="khub-consent-btn"
                  style={{ background: brandColor }}
                  onClick={(e) => { e.stopPropagation(); acceptScreenShare(); }}
                >
                  {modalConfig.allowLabel}
                </button>
                <button
                  className="khub-consent-cancel"
                  onClick={(e) => { e.stopPropagation(); declineScreenShare("user_declined"); }}
                >
                  Not now
                </button>
              </div>
            );
          })()}

          {/* ── Remote-control consent prompt ──────────────────────────── */}
          {remoteControlStatus === "requesting" && (
            <div
              className="khub-consent-overlay"
              style={{ zIndex: 70 }}
              onClick={(e) => { e.stopPropagation(); declineRemoteControl(); }}
            >
              <div
                className="khub-consent-logo"
                style={{ background: brandColor }}
                onClick={(e) => e.stopPropagation()}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3l7 19 2-8 8-2z" />
                </svg>
              </div>
              <h3 onClick={(e) => e.stopPropagation()}>Let the agent control your screen?</h3>
              <p onClick={(e) => e.stopPropagation()}>
                <strong>{screenShareAgentName || "The agent"}</strong> wants to
                click and type for you so they can show you how to do
                something. They can only act on this page. You can stop at any
                time by pressing <strong>Esc</strong>.
              </p>
              <button
                className="khub-consent-btn"
                style={{ background: brandColor }}
                onClick={(e) => { e.stopPropagation(); acceptRemoteControl(); }}
              >
                Allow control
              </button>
              <button
                className="khub-consent-cancel"
                onClick={(e) => { e.stopPropagation(); declineRemoteControl(); }}
              >
                Not now
              </button>
            </div>
          )}

          {/* ── Active media-session banner ─────────────────────────────── */}
          {(screenShareStatus === "live" || screenShareStatus === "connecting") && (() => {
            const includesVoice = screenShareMode === "voice" || screenShareMode === "both";
            const bannerLabel =
              screenShareStatus === "connecting" ? "Connecting…" :
              screenShareMode === "voice" ? "On a voice call with the agent" :
              screenShareMode === "both" ? "On call · sharing your screen" :
              "You're sharing your screen with the agent";
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  padding: "8px 12px",
                  background: "#fef2f2",
                  borderBottom: "1px solid #fecaca",
                  color: "#b91c1c",
                  fontSize: 12,
                  fontWeight: 500,
                  position: "relative",
                  zIndex: 5,
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: "#ef4444", animation: "pulse 1.5s infinite",
                  }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {bannerLabel}
                  </span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  {includesVoice && screenShareStatus === "live" && (
                    <button
                      onClick={toggleScreenShareMute}
                      title={screenShareIsMuted ? "Unmute" : "Mute"}
                      style={{
                        padding: "4px 8px",
                        border: "none",
                        borderRadius: 6,
                        background: screenShareIsMuted ? "#fde68a" : "#fee2e2",
                        color: screenShareIsMuted ? "#92400e" : "#b91c1c",
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      {screenShareIsMuted ? (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="1" y1="1" x2="23" y2="23" />
                          <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                          <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                          <line x1="12" y1="19" x2="12" y2="23" />
                        </svg>
                      ) : (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                          <line x1="12" y1="19" x2="12" y2="23" />
                        </svg>
                      )}
                      <span>{screenShareIsMuted ? "Muted" : "Mute"}</span>
                    </button>
                  )}
                  <button
                    onClick={() => stopScreenShare("manual")}
                    style={{
                      padding: "4px 10px",
                      border: "none",
                      borderRadius: 6,
                      background: "#dc2626",
                      color: "white",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {screenShareMode === "voice" ? "End" : "Stop"}
                  </button>
                </span>
                {/* Audio playback is portaled to document.body via remoteAudioRef
                   so it persists when the widget is minimized/closed */}
              </div>
            );
          })()}

          {/* Header */}
          <div className="khub-header">
            <div className="khub-header-top">
              <div className="khub-logo">
                {view !== "tabs" && (
                  <button
                    className="khub-back-btn"
                    onClick={() => { setView("tabs"); setShowEmoji(false); setChatEnded(false); setConfirmEnd(false); setConfirmLiveEnd(false); }}
                    aria-label="Back"
                  >
                    ←
                  </button>
                )}
                <img
                  src={toAbsoluteUrl("/media/logos/khub-logo-light.png")}
                  alt="K-HUB"
                  className="khub-header-brand"
                />
              </div>
              <div className="khub-header-actions">
                <div
                  className="khub-avatar khub-avatar--lg"
                  style={{
                    background: user?.entity?.image ? "transparent" : brandColor,
                    overflow: "hidden",
                  }}
                >
                  {user?.entity?.image ? (
                    <img
                      src={user.entity.image}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    getUserInitials(user)
                  )}
                </div>
                <button className="khub-close-btn khub-close-btn--plain" onClick={handleMinimize} aria-label="Minimize chat" title="Minimize">
                  <IconMinimize />
                </button>
                <button className="khub-close-btn khub-close-btn--plain" onClick={handleCloseWidget} aria-label="Close chat" title="Close">
                  <IconClose />
                </button>
              </div>
            </div>
            {view === "tabs" && (activeTab === "home" || activeTab === "messages") && (
              <div className="khub-greeting">
                Hi {getUserFirstName(user)} 👋<br />How can we help?
              </div>
            )}
            {view === "chat" && (
              <div className="khub-greeting" style={{ fontSize: "16px" }}>AI Assistant</div>
            )}
            {view === "live" && (
              <div className="khub-greeting" style={{ fontSize: "16px" }}>Live Support</div>
            )}
          </div>

          {/* Body */}
          <div className="khub-body">

            {/* ── HOME TAB ── */}
            {view === "tabs" && activeTab === "home" && (
              <div className="khub-home">
                {/* Agent reach-out card — pinned on Home until the user
                    actually reads the message. Two states:
                      • pending  → Accept / Read-later buttons
                      • ignored  → persistent "Read later" card with a single
                                   Read-message CTA (deferred-read state). */}
                {agentReachOut && (
                  <div
                    className="khub-card khub-card--reach-out"
                    style={{
                      borderColor: brandColor,
                      borderWidth: 2,
                      borderStyle: "solid",
                      display: "block",
                      cursor: "default",
                      padding: 14,
                    }}
                  >
                    {(() => {
                      // Show the Read-message single-CTA layout whenever the
                      // user has not yet read the message — either because
                      // they explicitly deferred (acknowledged === "ignored")
                      // or because the agent has stepped away while the user
                      // hadn't responded (agent_ended_at set on a pending
                      // reach-out). In both cases "Chat now" doesn't make
                      // sense as a CTA — there's no live agent to chat with.
                      const showReadMessage =
                        agentReachOut.acknowledged === "ignored" || !!agentReachOut.agent_ended_at;
                      return (
                        <>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                            <div
                              className="khub-card-icon"
                              style={{
                                background: agentReachOut.agentAvatar ? "transparent" : brandColor,
                                flexShrink: 0,
                                overflow: "hidden",
                                padding: 0,
                              }}
                            >
                              {agentReachOut.agentAvatar ? (
                                <img
                                  src={agentReachOut.agentAvatar}
                                  alt={agentReachOut.agentName || "Agent"}
                                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit", display: "block" }}
                                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                              ) : (
                                (agentReachOut.agentName || "A").charAt(0).toUpperCase()
                              )}
                            </div>
                            <div style={{ flex: 1, textAlign: "left" }}>
                              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                                {showReadMessage
                                  ? `Message from ${agentReachOut.agentName} waiting for you`
                                  : agentReachOut.reopened
                                    ? `${agentReachOut.agentName} reopened your chat`
                                    : `${agentReachOut.agentName} sent you a message`}
                              </h4>
                              <p style={{ margin: "6px 0 0", fontSize: 13, color: "#555" }}>
                                {agentReachOut.preview}
                              </p>
                              {agentReachOut.acknowledged === "ignored" && !agentReachOut.agent_ended_at && (
                                <p style={{ margin: "6px 0 0", fontSize: 11, color: "#999", fontStyle: "italic" }}>
                                  You chose Read later — open the chat whenever you're ready.
                                </p>
                              )}
                            </div>
                          </div>
                          {showReadMessage ? (
                      <div style={{ marginTop: 12 }}>
                        <button
                          type="button"
                          onClick={() => acknowledgeReachOut("accept")}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            background: brandColor,
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            fontWeight: 600,
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          Read message
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                        <button
                          type="button"
                          onClick={() => acknowledgeReachOut("accept")}
                          style={{
                            flex: 1,
                            padding: "8px 12px",
                            background: brandColor,
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            fontWeight: 600,
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          Chat now
                        </button>
                        <button
                          type="button"
                          onClick={() => acknowledgeReachOut("ignore")}
                          style={{
                            flex: 1,
                            padding: "8px 12px",
                            background: "transparent",
                            color: "#666",
                            border: "1px solid #d0d0d0",
                            borderRadius: 8,
                            fontWeight: 600,
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          Read later
                        </button>
                      </div>
                    )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Live chat resume card — shown whenever a live session is
                    in flight (active / waiting / submitted async), so the
                    user can always find their way back to the conversation.
                    Hidden while a reach-out banner is up — that banner is the
                    primary CTA in that state. */}
                {!agentReachOut && (livePhase === "active" || livePhase === "waiting" ||
                  (livePhase === "async" && asyncStep === "submitted")) && (
                  <button
                    className="khub-card khub-card--resume"
                    onClick={() => { setView("live"); setAgentAlert(null); }}
                    style={{ borderColor: brandColor }}
                  >
                    <div className="khub-card-text">
                      <h4>
                        {livePhase === "active"
                          ? "Live chat in progress"
                          : livePhase === "waiting"
                            ? "You're in the queue"
                            : "Offline request submitted"}
                      </h4>
                      <p>
                        {livePhase === "active"
                          ? "Tap to continue chatting with your agent"
                          : livePhase === "waiting"
                            ? "Tap to view your place in the queue"
                            : "We'll notify you here when an agent picks it up"}
                      </p>
                    </div>
                    <div className="khub-card-icon" style={{ background: brandColor }}>
                      💬
                    </div>
                  </button>
                )}

                {/* Ask a question card */}
                <button className="khub-card" onClick={() => openChatWith("")}>
                  <div className="khub-card-text">
                    <h4>Ask a question</h4>
                    <p>AI Agent and team can help</p>
                  </div>
                  <div className="khub-card-icon-stack">
                    <div
                      className="khub-card-avatar-peek"
                      style={{
                        background: user?.entity?.image ? "transparent" : brandColor,
                        overflow: "hidden",
                      }}
                    >
                      {user?.entity?.image ? (
                        <img
                          src={user.entity.image}
                          alt=""
                          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                      ) : (
                        <span className="khub-card-avatar-peek__initial">{getUserInitials(user)}</span>
                      )}
                    </div>
                    <div
                      className="khub-card-icon"
                      style={{ background: "#121330", padding: 6, overflow: "hidden" }}
                    >
                      <img
                        src={toAbsoluteUrl("/media/logos/khub-logo-light.png")}
                        alt="Khub"
                        style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                      />
                    </div>
                  </div>
                </button>

                {/* FAQ quick questions */}
                <div className="khub-faq-section">
                  <p className="khub-faq-label">Quick help</p>
                  {questions.map((q) => (
                    <button key={q.id} className="khub-faq-item" onClick={() => handleFaqClick(q)}>
                      <span>{q.question}</span>
                      <IconChevron />
                    </button>
                  ))}
                </div>

              </div>
            )}

            {/* ── K-NEWS TAB ── */}
            {view === "tabs" && activeTab === "news" && (
              <div className="khub-news">
                {newsLoading && broadcasts.length === 0 ? (
                  <div className="khub-news-empty">
                    <p className="khub-empty-sub">Loading announcements…</p>
                  </div>
                ) : broadcasts.length === 0 ? (
                  <div className="khub-news-empty">
                    <div className="khub-empty-icon">📰</div>
                    <p className="khub-empty-title">No announcements yet</p>
                    <p className="khub-empty-sub">
                      We&apos;ll post updates and product news here.
                    </p>
                  </div>
                ) : (
                  <div className="khub-news-list">
                    <div className="khub-news-list-header">
                      <p className="khub-news-list-header-eyebrow">Latest</p>
                      <p className="khub-news-list-header-title">
                        From the Kommerce Hub team
                      </p>
                    </div>
                    {broadcasts.map((bc) => (
                      <button
                        key={bc.id}
                        className={`khub-news-card${bc.is_read ? "" : " unread"}`}
                        style={bc.is_read ? undefined : { borderLeftColor: brandColor }}
                        onClick={() => openBroadcast(bc)}
                      >
                        {bc.banner_image_url && (
                          <div className="khub-news-card-media">
                            <img
                              src={resolveMediaUrl(bc.banner_image_url)}
                              alt=""
                              onError={(e) => {
                                e.currentTarget.parentElement.style.display = "none";
                              }}
                            />
                          </div>
                        )}
                        <div className="khub-news-card-body">
                          <div className="khub-news-card-tags">
                            {!bc.is_read && (
                              <span
                                className="khub-news-pill khub-news-pill-new"
                                style={{ background: brandColor }}
                              >
                                New
                              </span>
                            )}
                            <span
                              className={`khub-news-pill khub-news-pill-${bc.priority}`}
                            >
                              {bc.priority}
                            </span>
                            {bc.requires_acknowledgement && !bc.is_acknowledged && (
                              <span className="khub-news-pill khub-news-pill-ack">
                                Ack required
                              </span>
                            )}
                            {Array.isArray(bc.tags) && bc.tags.map((tag) => (
                              <span key={tag} className="khub-news-tag">{tag}</span>
                            ))}
                          </div>
                          <h4 className="khub-news-card-title">{bc.title}</h4>
                          <p className="khub-news-card-preview">{newsSnippet(bc)}</p>
                          <div className="khub-news-card-footer">
                            <span className="khub-news-card-meta">
                              {bc.sent_at
                                ? formatBroadcastTime(bc.sent_at)
                                : ""}
                            </span>
                            <IconChevron />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── MESSAGES TAB ── */}
            {view === "tabs" && activeTab === "messages" && (
              <div className="khub-messages-tab">
                {(livePhase === "active" || livePhase === "waiting" ||
                  (livePhase === "async" && asyncStep === "submitted")) ? (
                  <div className="khub-conversation-list">
                    <button
                      className="khub-conversation-item"
                      onClick={() => { setView("live"); setAgentAlert(null); }}
                    >
                      <div
                        className="khub-conversation-avatar"
                        style={{ background: brandColor }}
                      >
                        {livePhase === "active" ? "A" : "⏳"}
                      </div>
                      <div className="khub-conversation-body">
                        <div className="khub-conversation-title">
                          {livePhase === "active"
                            ? "Live Support"
                            : livePhase === "waiting"
                              ? "Waiting for an agent"
                              : "Offline request submitted"}
                        </div>
                        <div className="khub-conversation-preview">
                          {livePhase === "active"
                            ? (liveMessages.length > 0
                                ? (liveMessages[liveMessages.length - 1].message || "Tap to continue")
                                : "Tap to continue chatting")
                            : livePhase === "waiting"
                              ? "An agent will join shortly"
                              : "We'll notify you here when an agent picks it up"}
                        </div>
                      </div>
                      {agentAlert?.unread > 0 && (
                        <span
                          className="khub-conversation-unread"
                          style={{ background: brandColor }}
                        >
                          {agentAlert.unread}
                        </span>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="khub-empty-state">
                    <div className="khub-empty-icon">💬</div>
                    <p className="khub-empty-title">No conversations yet</p>
                    <p className="khub-empty-sub">Start a chat to get help from our team.</p>
                    <button
                      className="khub-start-conv-btn"
                      style={{ background: brandColor }}
                      onClick={() => openChatWith("")}
                    >
                      Start a conversation
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── RECENT TAB ── */}
            {view === "tabs" && activeTab === "recent" && (
              <div className="khub-home" style={{ overflowY: "auto", flex: 1 }}>
                {recentSessions.length > 0 ? (
                  <div className="khub-faq-section" style={{ marginTop: 0 }}>
                    <p className="khub-faq-label">Recent conversations</p>
                    {recentSessions.map((s) => {
                      // Backend only returns closed sessions here; active /
                      // waiting / pending-reach-out chats live on Home.
                      const sender = s.last_message_sender_type;
                      const senderLabel = sender === "agent"
                        ? (s.assigned_agent_name || "Agent")
                        : sender === "user"
                          ? "You"
                          : sender === "system"
                            ? ""
                            : "";
                      const previewText = s.last_message_preview
                        ? (senderLabel ? `${senderLabel}: ${s.last_message_preview}` : s.last_message_preview)
                        : "View transcript or reopen";
                      const timestamp = formatSessionDateTime(
                        s.last_message_at || s.ended_at || s.started_at || s.created_at
                      );
                      return (
                        <button
                          key={s.id}
                          className="khub-faq-item"
                          onClick={() => resumeRecentSession(s)}
                          style={{ alignItems: "flex-start" }}
                        >
                          <span style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, textAlign: "left" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#777", flexWrap: "wrap" }}>
                              <span
                                style={{
                                  width: 6, height: 6, borderRadius: "50%",
                                  background: "#bbb",
                                }}
                              />
                              Closed
                              {s.assigned_agent_name && ` · ${s.assigned_agent_name}`}
                              {timestamp && ` · ${timestamp}`}
                            </span>
                            <span style={{ fontSize: 13, color: "#333", lineHeight: 1.3 }}>
                              {previewText}
                            </span>
                          </span>
                          <IconChevron />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="khub-empty-state">
                    <div className="khub-empty-icon">🕒</div>
                    <p className="khub-empty-title">No recent conversations</p>
                    <p className="khub-empty-sub">Chats with our agents from the last 45 days will show up here.</p>
                    <button
                      className="khub-start-conv-btn"
                      style={{ background: brandColor }}
                      onClick={() => openChatWith("")}
                    >
                      Start a conversation
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── CHATBOT VIEW ── */}
            {view === "chat" && (
              <div className="khub-chat">
                <div className="khub-messages">
                  {messages.map((m, i) => {
                    if (m.role === "system") {
                      return <div key={m.id || i} className="khub-system-msg"><span>{m.content}</span></div>;
                    }
                    const isBot = m.role === "assistant";
                    const isLast = i === messages.length - 1;
                    const actions = isBot && Array.isArray(m.suggested_actions)
                      ? m.suggested_actions.filter((a) => a !== "Talk to a human agent")
                      : [];
                    return (
                      <div key={m.id || i}>
                        <div
                          className={`khub-msg ${m.role}`}
                          style={m.role === "user" ? { background: brandColor } : {}}
                          {...(isBot
                            ? { dangerouslySetInnerHTML: { __html: parseMarkdown(m.content) } }
                            : { children: m.content }
                          )}
                        />
                        {isBot && actions.length > 0 && isLast && !loading && (
                          <div className="khub-actions">
                            {actions.map((a) => (
                              <button
                                key={a}
                                className="khub-action-chip"
                                style={{ borderColor: brandColor, color: brandColor }}
                                onClick={() => handleSuggestedAction(a)}
                                disabled={sessionClosed && a !== "Start a new chat"}
                              >
                                {a}
                              </button>
                            ))}
                          </div>
                        )}
                        {isBot && typeof m.id === "number" && (() => {
                          const fb = feedback[m.id]
                            || (m.is_helpful === true ? "up" : m.is_helpful === false ? "down" : null);
                          if (fb) {
                            return (
                              <div className="khub-feedback">
                                <span className="khub-feedback-thanks">
                                  {fb === "up" ? "👍 Thanks for your feedback" : "👎 Thanks — we'll improve this"}
                                </span>
                              </div>
                            );
                          }
                          return (
                            <div className="khub-feedback">
                              <span className="khub-feedback-label">Was this helpful?</span>
                              <button
                                className="khub-feedback-btn"
                                title="Helpful"
                                aria-label="Mark this answer helpful"
                                onClick={() => sendFeedback(m.id, true)}
                              >👍</button>
                              <button
                                className="khub-feedback-btn"
                                title="Not helpful"
                                aria-label="Mark this answer not helpful"
                                onClick={() => sendFeedback(m.id, false)}
                              >👎</button>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                  {loading && <div className="khub-typing"><span /><span /><span /></div>}
                  {error && <div className="khub-error-banner">{error}</div>}
                  {chatEnded && (
                    <div style={{ textAlign: "center", padding: "12px 0" }}>
                      <button
                        className="khub-action-chip"
                        style={{ borderColor: brandColor, color: brandColor }}
                        onClick={handleStartNewChat}
                      >
                        Go home
                      </button>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>


                {!chatEnded && (
                  <>
                    {!sessionClosed && (
                      <div className="khub-end-chat-bar">
                        <button className="khub-end-chat-btn" onClick={() => setConfirmEnd(true)}>
                          <IconPower /> End this chat
                        </button>
                      </div>
                    )}

                    {/* Emoji picker */}
                    {showEmoji && (
                      <div className="khub-emoji-picker">
                        {COMMON_EMOJIS.map((e) => (
                          <button key={e} className="khub-emoji-btn" onClick={() => insertEmoji(e)}>{e}</button>
                        ))}
                      </div>
                    )}

                    <div className="khub-input-bar">
                      <button
                        className={`khub-media-btn ${showEmoji ? "active" : ""}`}
                        onClick={() => setShowEmoji((v) => !v)}
                        title="Emoji"
                        style={showEmoji ? { color: brandColor } : {}}
                      >😊</button>
                      <textarea
                        ref={inputRef}
                        className="khub-input"
                        rows={1}
                        placeholder={sessionClosed ? "Session ended. Start a new chat." : "Type a message…"}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={loading || escalated || sessionClosed}
                      />
                      <button
                        className="khub-send-btn"
                        style={{ background: brandColor }}
                        onClick={sendMessage}
                        disabled={!input.trim() || loading || escalated || sessionClosed}
                        aria-label="Send"
                      >
                        <IconSend />
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── LIVE CHAT VIEW ── */}
            {view === "live" && (
              <div className="khub-chat">
                {liveAgent && (
                  <div
                    className="khub-live-agent-header"
                    role="region"
                    aria-label={`Chatting with ${liveAgent.name}`}
                  >
                    {liveAgent.avatarUrl ? (
                      <img
                        className="khub-live-agent-header__avatar"
                        src={liveAgent.avatarUrl}
                        alt=""
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <span
                        className="khub-live-agent-header__avatar khub-live-agent-header__avatar--fallback"
                        style={{ background: brandColor }}
                        aria-hidden="true"
                      >
                        {liveAgent.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                    <div className="khub-live-agent-header__text">
                      <span className="khub-live-agent-header__name">{liveAgent.name}</span>
                      <span className="khub-live-agent-header__role">Customer Support</span>
                    </div>
                  </div>
                )}
                <div className="khub-messages">
                  {livePhase === "idle" && (
                    <div className="khub-live-idle">
                      <div className="khub-live-idle-icon">💬</div>
                      <p>Connect with a live support agent for immediate help.</p>
                      {liveError && <p className="khub-live-error">{liveError}</p>}
                      <button
                        className="khub-live-start-btn"
                        style={{ background: brandColor }}
                        onClick={requestAgent}
                      >
                        Chat with Agent
                      </button>
                    </div>
                  )}

                  {/* Original "Connecting…" placeholder — only when there's
                      no transcript yet (fresh user-initiated request).
                      For a re-queued chat that already has messages, we
                      keep the transcript visible and overlay an inline
                      banner above it (rendered further down in the active /
                      ended branch). */}
                  {livePhase === "waiting" && liveMessages.length === 0 && (
                    <div className="khub-live-idle">
                      <div className="khub-live-idle-icon" style={{ animation: "khub-bounce-slow 1.4s infinite" }}>⏳</div>
                      <p style={{ fontWeight: 600 }}>Connecting you to an agent…</p>
                      {queueInfo && (
                        <>
                          <p className="khub-queue-info">Position: <strong>{queueInfo.position}</strong></p>
                          <p className="khub-queue-info">~{Math.ceil(queueInfo.estimated_wait_seconds / 60)} min wait</p>
                          {queueInfo.agents_online === 0 && (
                            <p style={{ color: "#e65100", fontSize: "12px" }}>No agents online right now.</p>
                          )}
                        </>
                      )}
                      {showLongWaitPrompt && (
                        <div
                          className="khub-long-wait-prompt"
                          style={{
                            marginTop: 16,
                            padding: 12,
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            background: "#fafafa",
                            textAlign: "center",
                          }}
                        >
                          <p style={{ margin: 0, fontSize: 13, color: "#374151" }}>
                            Our agents are still busy. You can keep waiting, or
                            leave a message and we'll reply once agents are back.
                          </p>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              justifyContent: "center",
                              marginTop: 10,
                              flexWrap: "wrap",
                            }}
                          >
                            <button
                              className="khub-action-chip"
                              style={{ borderColor: brandColor, color: brandColor }}
                              onClick={() => setShowLongWaitPrompt(false)}
                            >Keep waiting</button>
                            <button
                              className="khub-action-chip"
                              style={{ borderColor: brandColor, color: brandColor }}
                              onClick={leaveMessageAndStartOffline}
                            >Leave a message</button>
                          </div>
                        </div>
                      )}
                      <button className="khub-leave-queue-btn" onClick={endLiveChat}>Leave queue</button>
                    </div>
                  )}

                  {livePhase === "async" && (
                    <>
                      {asyncMessages.map((m, i) => {
                        const isLast = i === asyncMessages.length - 1;
                        return (
                          <div key={m.id}>
                            <div
                              className={`khub-msg ${m.role}`}
                              style={m.role === "user" ? { background: brandColor } : {}}
                            >
                              {m.content}
                            </div>
                            {isLast && m.actions && asyncStep === "askPriority" && (
                              <div className="khub-actions">
                                <button
                                  className="khub-action-chip"
                                  style={{ borderColor: brandColor, color: brandColor }}
                                  onClick={() => pickAsyncPriority(true)}
                                >Yes (High Priority)</button>
                                <button
                                  className="khub-action-chip"
                                  style={{ borderColor: brandColor, color: brandColor }}
                                  onClick={() => pickAsyncPriority(false)}
                                >No (Normal)</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {asyncStep === "submitting" && (
                        <div className="khub-typing"><span /><span /><span /></div>
                      )}
                      {asyncStep === "submitted" && (
                        <div className="khub-async-summary" style={{ borderColor: brandColor }}>
                          <div className="khub-async-summary-row">
                            <span className="khub-async-summary-label">Issue</span>
                            <span className="khub-async-summary-value">{asyncIssue}</span>
                          </div>
                          <div className="khub-async-summary-row">
                            <span className="khub-async-summary-label">Priority</span>
                            <span className="khub-async-summary-value">{asyncUrgent ? "High" : "Normal"}</span>
                          </div>
                        </div>
                      )}
                      {asyncStep === "submitted" && (
                        <div style={{ textAlign: "center", padding: "12px 0" }}>
                          <button
                            className="khub-action-chip"
                            style={{ borderColor: brandColor, color: brandColor }}
                            onClick={() => {
                              // User wants to go back — keep the async session alive so
                              // the WS handler can flip to "active" when an agent accepts.
                              setView("tabs");
                              setActiveTab("home");
                            }}
                          >Back to home</button>
                        </div>
                      )}
                    </>
                  )}

                  {/* Transcript view — covers active, ended, and the
                      "re-queued with prior messages" case (waiting +
                      messages exist). The user-initiated request flow
                      handles its own waiting placeholder above. */}
                  {(livePhase === "active" || livePhase === "ended" ||
                    (livePhase === "waiting" && liveMessages.length > 0)) && (
                    <>
                      {livePhase === "waiting" && liveMessages.length > 0 &&
                        queueInfo?.agents_online !== 0 && (
                        // Re-queue waiting banner — online happy path only.
                        // The offline variant renders just above the input bar
                        // (closer to where the user is typing) so they can see
                        // "agents offline, but I can still send" without
                        // scrolling. Until queueInfo lands (first poll),
                        // default to the online copy — better than flashing
                        // "offline" in the brief gap before the first poll.
                        <div
                          style={{
                            margin: "8px 14px 12px",
                            padding: "10px 12px",
                            border: `1px dashed ${brandColor}`,
                            background: "#fff8e6",
                            borderRadius: 10,
                            fontSize: 13,
                            color: "#553",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                          }}
                        >
                          <span style={{ fontSize: 16, lineHeight: "20px" }}>⏳</span>
                          <span>Connecting you to an agent…</span>
                        </div>
                      )}
                      {botHistory.length > 0 && (
                        <div className="khub-bot-history">
                          <button
                            type="button"
                            className={`khub-bot-history-toggle${showBotHistory ? " is-open" : ""}`}
                            onClick={() => setShowBotHistory((v) => !v)}
                            aria-expanded={showBotHistory}
                          >
                            <span className="khub-bot-history-title">
                              <span className="khub-bot-history-icon" aria-hidden="true">
                                <IconChat />
                              </span>
                              Previous bot conversation
                              <span className="khub-bot-history-count">{botHistory.length}</span>
                            </span>
                            <span className="khub-bot-history-chevron" aria-hidden="true">
                              <IconChevron />
                            </span>
                          </button>
                          {showBotHistory && (
                            <div className="khub-bot-history-body">
                              {botHistory.map((m) => (
                                <div
                                  key={`bh-${m.id}`}
                                  className={`khub-msg ${m.role === "user" ? "user" : "assistant"}`}
                                  style={m.role === "user" ? { background: brandColor } : {}}
                                >
                                  {m.content}
                                </div>
                              ))}
                              <div className="khub-bot-history-divider">
                                <span>Live chat started</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {liveMessages.map((m) => <LiveMessage key={m.id} msg={m} brandColor={brandColor} />)}
                      {agentTyping && (
                        <div className="khub-typing"><span /><span /><span /></div>
                      )}
                      {/* Long-wait prompt for active chats — fires when the user's
                          last message has been unanswered for ≥ 2 min. Same offline
                          fallback as the queue-side prompt. */}
                      {livePhase === "active" && showLongWaitPrompt && (
                        <div
                          className="khub-long-wait-prompt"
                          style={{
                            margin: "8px 14px 12px",
                            padding: 12,
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            background: "#fafafa",
                            textAlign: "center",
                          }}
                        >
                          <p style={{ margin: 0, fontSize: 13, color: "#374151" }}>
                            No response from the agent yet. You can keep waiting,
                            or leave a message and we'll reply as soon as someone picks it up.
                          </p>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              justifyContent: "center",
                              marginTop: 10,
                              flexWrap: "wrap",
                            }}
                          >
                            <button
                              className="khub-action-chip"
                              style={{ borderColor: brandColor, color: brandColor }}
                              onClick={() => {
                                setShowLongWaitPrompt(false);
                                setUserMsgPendingAt(null);
                              }}
                            >Keep waiting</button>
                            <button
                              className="khub-action-chip"
                              style={{ borderColor: brandColor, color: brandColor }}
                              onClick={leaveMessageAndStartOffline}
                            >Leave a message</button>
                          </div>
                        </div>
                      )}
                      {livePhase === "ended" && (
                        <div style={{ display: "flex", gap: 8, justifyContent: "center", padding: "12px 0", flexWrap: "wrap" }}>
                          <button
                            className="khub-action-chip"
                            style={{ background: brandColor, color: "#fff", borderColor: brandColor }}
                            onClick={reopenLiveChat}
                          >Reopen chat</button>
                          <button
                            className="khub-action-chip"
                            style={{ borderColor: brandColor, color: brandColor }}
                            onClick={() => {
                              // Reset live chat state
                              setLivePhase("idle");
                              setLiveMessages([]);
                              setBotHistory([]);
                              setLiveSessionId(null);
                              setAsyncStep(null);
                              setAsyncMessages([]);
                              setAsyncIssue("");
                              setAsyncUrgent(null);
                              setAsyncInput("");
                              // Clear the bot session too — the whole conversation is done.
                              // The backend already closed the linked chatbot session when
                              // live chat ended, so we just need to discard local state.
                              localStorage.removeItem("khub_bot_session_id");
                              setSession(null);
                              setMessages([]);
                              setChatEnded(false);
                              setSessionClosed(false);
                              setEscalated(false);
                              setShowEscalateBar(false);
                              setView("tabs");
                              setActiveTab("recent");
                            }}
                          >Go home</button>
                        </div>
                      )}
                    </>
                  )}

                  {liveError && livePhase !== "idle" && (
                    <div className="khub-error-banner">{liveError}</div>
                  )}
                  <div ref={liveEndRef} />
                </div>

                {livePhase === "async" && asyncStep === "askIssue" && (
                  <div className="khub-input-bar">
                    <textarea
                      className="khub-input"
                      rows={1}
                      placeholder="Describe your issue…"
                      value={asyncInput}
                      onChange={(e) => setAsyncInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleAsyncInputSubmit();
                        }
                      }}
                      autoFocus
                    />
                    <button
                      className="khub-send-btn"
                      style={{ background: brandColor }}
                      onClick={handleAsyncInputSubmit}
                      disabled={!asyncInput.trim()}
                      aria-label="Send"
                    >
                      <IconSend />
                    </button>
                  </div>
                )}

                {/* Soft-close: agent stepped away on a Read-later (or
                    untouched) session. The user has just opened the chat
                    (acknowledge accepted). Show End-chat + a single
                    Chat-now CTA — no inline banner; the transcript itself
                    is the context. After tapping, branch on availability. */}
                {livePhase === "active" && liveAgentEndedAt && !softCloseMode && (
                  <>
                    <div className="khub-end-chat-bar">
                      <button className="khub-end-chat-btn" onClick={() => setConfirmLiveEnd(true)}>
                        <IconPower /> End this chat
                      </button>
                    </div>
                    <div style={{ padding: "10px 14px 14px" }}>
                      <button
                        type="button"
                        onClick={softCloseStartChat}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          background: brandColor,
                          color: "#fff",
                          border: "none",
                          borderRadius: 10,
                          fontWeight: 600,
                          fontSize: 14,
                          cursor: "pointer",
                        }}
                      >
                        Chat now
                      </button>
                    </div>
                  </>
                )}

                {/* Soft-close — async branch: no agents online. Single
                    textarea + "Send and close" so the user can leave a
                    final message and have the session moved to Recent. */}
                {livePhase === "active" && softCloseMode === "async" && (
                  <>
                    <div
                      style={{
                        margin: "10px 14px 0",
                        padding: "10px 12px",
                        border: "1px solid #d0d0d0",
                        background: "#f8f8f8",
                        borderRadius: 10,
                        fontSize: 13,
                        color: "#555",
                      }}
                    >
                      No agents are online right now. Leave your message below and tap
                      <strong> Send and close</strong> — we'll save it for the team.
                    </div>
                    <div className="khub-input-bar">
                      <textarea
                        className="khub-input"
                        rows={2}
                        placeholder="Type your message…"
                        value={softCloseAsyncInput}
                        onChange={(e) => setSoftCloseAsyncInput(e.target.value)}
                        disabled={softCloseSubmitting}
                        autoFocus
                      />
                    </div>
                    <div style={{ padding: "0 14px 14px" }}>
                      <button
                        type="button"
                        onClick={softCloseSendAndClose}
                        disabled={!softCloseAsyncInput.trim() || softCloseSubmitting}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          background: brandColor,
                          color: "#fff",
                          border: "none",
                          borderRadius: 10,
                          fontWeight: 600,
                          fontSize: 14,
                          opacity: !softCloseAsyncInput.trim() || softCloseSubmitting ? 0.5 : 1,
                          cursor: !softCloseAsyncInput.trim() || softCloseSubmitting ? "default" : "pointer",
                        }}
                      >
                        {softCloseSubmitting ? "Sending…" : "Send and close"}
                      </button>
                    </div>
                  </>
                )}

                {/* Normal active-chat composer. Hidden during the soft-close
                    pre-tap state and during the async branch — but shown
                    during a regular chat, when the user taps Chat now and
                    agents are online (softCloseMode === "live"), AND in
                    the re-queued waiting state (status flipped to WAITING
                    after a soft-close reply, but the user must still be
                    able to keep typing and end the chat at will). */}
                {(
                  (livePhase === "active" && (!liveAgentEndedAt || softCloseMode === "live")) ||
                  (livePhase === "waiting" && liveMessages.length > 0)
                ) && (
                  <>
                    <div className="khub-end-chat-bar">
                      <button className="khub-end-chat-btn" onClick={() => setConfirmLiveEnd(true)}>
                        <IconPower /> End this chat
                      </button>
                    </div>

                    {showEmoji && (
                      <div className="khub-emoji-picker">
                        {COMMON_EMOJIS.map((e) => (
                          <button key={e} className="khub-emoji-btn" onClick={() => insertEmoji(e)}>{e}</button>
                        ))}
                      </div>
                    )}
                    {/* Offline notice anchored to the composer — sits right
                        above the input so the user sees it as context for
                        "you can still type, agents will pick up later"
                        instead of as a top-of-transcript banner that
                        scrolls out of view. Only relevant while re-queued
                        (waiting + has prior messages); active chat with an
                        accepted agent doesn't need this. */}
                    {livePhase === "waiting" &&
                      liveMessages.length > 0 &&
                      queueInfo?.agents_online === 0 && (
                      <div
                        style={{
                          margin: "0 14px 8px",
                          padding: "8px 12px",
                          border: "1px dashed #d97706",
                          background: "#fef3c7",
                          borderRadius: 10,
                          fontSize: 12.5,
                          color: "#553",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 15, lineHeight: "18px" }}>💤</span>
                        <span>
                          All agents are offline right now. Keep typing — your messages
                          stay in the queue, and an agent will reply when they're back online.
                        </span>
                      </div>
                    )}
                    <div className="khub-input-bar khub-input-bar--live">
                      {/* Emoji */}
                      <button
                        className={`khub-media-btn ${showEmoji ? "active" : ""}`}
                        onClick={() => setShowEmoji((v) => !v)}
                        title="Emoji"
                        style={showEmoji ? { color: brandColor } : {}}
                      >😊</button>

                      {/* File */}
                      <button
                        className="khub-media-btn"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        title="Attach file"
                      ><IconAttach /></button>

                      <textarea
                        className="khub-input"
                        rows={1}
                        placeholder={uploading ? "Uploading…" : "Type a message…"}
                        value={liveInput}
                        onChange={handleLiveInputChange}
                        onKeyDown={handleLiveKeyDown}
                        disabled={uploading}
                      />
                      <button
                        className="khub-send-btn"
                        style={{ background: brandColor }}
                        onClick={sendLiveMessage}
                        disabled={!liveInput.trim() || uploading}
                        aria-label="Send"
                      >
                        <IconSend />
                      </button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="khub-file-input"
                      accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                      onChange={handleFileChange}
                    />
                  </>
                )}
              </div>
            )}
          </div>

          {/* Tab bar */}
          {view === "tabs" && (
            <div className="khub-tabs">
              <button
                className={`khub-tab ${activeTab === "home" ? "active" : ""}`}
                onClick={() => { setActiveTab("home"); setSelectedBroadcast(null); }}
                style={activeTab === "home" ? { color: brandColor } : {}}
              >
                <IconHome />Home
              </button>
              <button
                className={`khub-tab ${activeTab === "news" ? "active" : ""}`}
                onClick={() => setActiveTab("news")}
                style={activeTab === "news" ? { color: brandColor } : {}}
              >
                <span className="khub-tab-icon-wrap">
                  <IconNews />
                  {unseenCount > 0 && (
                    <span
                      className="khub-tab-badge"
                      style={{ background: brandColor }}
                    >
                      {unseenCount > 9 ? "9+" : unseenCount}
                    </span>
                  )}
                </span>
                K-News
              </button>
              <button
                className={`khub-tab ${activeTab === "messages" ? "active" : ""}`}
                onClick={() => { setActiveTab("messages"); setSelectedBroadcast(null); }}
                style={activeTab === "messages" ? { color: brandColor } : {}}
              >
                <IconMessages />Messages
              </button>
              <button
                className={`khub-tab ${activeTab === "recent" ? "active" : ""}`}
                onClick={() => { setActiveTab("recent"); setSelectedBroadcast(null); }}
                style={activeTab === "recent" ? { color: brandColor } : {}}
              >
                <IconRecent />Recent
              </button>
            </div>
          )}

          {/* Bot chat end confirmation */}
          {confirmEnd && (
            <div className="khub-confirm-overlay" onClick={() => setConfirmEnd(false)}>
              <div className="khub-confirm-card" onClick={(e) => e.stopPropagation()}>
                <div className="khub-confirm-icon"><IconPower /></div>
                <div className="khub-confirm-title">End this chat?</div>
                <div className="khub-confirm-desc">
                  Your conversation will be saved to Messages. You can start a new chat anytime.
                </div>
                <div className="khub-confirm-actions">
                  <button className="khub-confirm-cancel" onClick={() => setConfirmEnd(false)}>Keep chatting</button>
                  <button className="khub-confirm-end" onClick={handleEndBotChat}>End chat</button>
                </div>
              </div>
            </div>
          )}

          {/* Live chat end confirmation */}
          {confirmLiveEnd && (
            <div className="khub-confirm-overlay" onClick={() => setConfirmLiveEnd(false)}>
              <div className="khub-confirm-card" onClick={(e) => e.stopPropagation()}>
                <div className="khub-confirm-icon"><IconPower /></div>
                <div className="khub-confirm-title">End this chat?</div>
                <div className="khub-confirm-desc">
                  Your conversation will be saved to Messages. You can start a new chat anytime.
                </div>
                <div className="khub-confirm-actions">
                  <button className="khub-confirm-cancel" onClick={() => setConfirmLiveEnd(false)}>Keep chatting</button>
                  <button className="khub-confirm-end" onClick={() => { setConfirmLiveEnd(false); endLiveChat(); }}>End chat</button>
                </div>
              </div>
            </div>
          )}

          {/* Privacy note */}
          <div className="khub-privacy-note">
            By chatting with us, you agree to our{" "}
            <a
              href={`${CS_PORTAL_URL}/cs-api/chatbot/privacy-policy/page`}
              target="_blank"
              rel="noreferrer"
              style={{ color: brandColor }}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      )}

      {/* The primary launcher is the Metronic topbar "help" icon (see
          ChatbotHelpToggle + chatbotLauncherBridge). The panel header X
          (handleCloseWidget) closes back up to that icon; the minimize button
          (handleMinimize) collapses to the docked bubble below instead. */}

      {/* ── Minimized bubble ───────────────────────────────────────────────
          Shown only when the user minimized an open chat. Stays docked at the
          bottom; click to pop the same conversation back open. */}
      {(minimized || newsNag) && !isOpen && (
        <button
          className={`khub-launcher${
            agentArrivedBadge || unseenCount > 0 ? " khub-launcher--attention" : ""
          }${newsNag ? " khub-launcher--drop" : ""}`}
          onClick={() => handleLauncherClick()}
          aria-label={newsNag ? "Open K-News, new announcement" : "Open chat"}
          title={newsNag ? "New announcement" : "Open chat"}
        >
          <span
            className="khub-launcher-logo"
            role="img"
            aria-label="K-Hub"
            style={{ backgroundImage: `url(${toAbsoluteUrl("/media/logos/khub-logo-light.png")})` }}
          />
          {unreadCount > 0 && (
            <span className="khub-launcher-badge" aria-hidden="true">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
          {unreadCount === 0 && newsNag && unseenCount > 0 && (
            <span className="khub-launcher-badge khub-launcher-badge--news" aria-hidden="true">
              {unseenCount > 9 ? "9+" : unseenCount}
            </span>
          )}
        </button>
      )}

      {/* ── New-broadcast preview card (above the hopping bubble) ─────────── */}
      {newsAlert && !isOpen && (
        <div className="khub-news-alert" role="status">
          <div className="khub-news-alert-head">
            <span><i className="khub-news-alert-dot" aria-hidden="true" />New announcement</span>
            <span className="khub-news-alert-when">
              {newsAlert.sent_at ? formatBroadcastTime(newsAlert.sent_at) : "Kommerce Hub"}
            </span>
          </div>
          <div className="khub-news-alert-body">
            <div className="khub-news-card-tags">
              <span className={`khub-news-pill khub-news-pill-${newsAlert.priority}`}>
                {newsAlert.priority}
              </span>
              {Array.isArray(newsAlert.tags) && newsAlert.tags.slice(0, 2).map((tag) => (
                <span key={tag} className="khub-news-tag">{tag}</span>
              ))}
            </div>
            <h4 className="khub-news-alert-title">{newsAlert.title}</h4>
            <p className="khub-news-alert-preview">{newsSnippet(newsAlert)}</p>
            <div className="khub-news-alert-actions">
              <button
                className="khub-news-alert-read"
                style={{ background: brandColor }}
                onClick={() => handleLauncherClick(newsAlert)}
              >
                Read now
              </button>
              <button className="khub-news-alert-later" onClick={() => setNewsAlert(null)}>
                Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Announcement reader modal (portaled above the whole app) ────── */}
      {selectedBroadcast && isOpen && typeof document !== "undefined" &&
        createPortal(
          <BroadcastModal
            bc={selectedBroadcast}
            list={broadcasts}
            brandColor={brandColor}
            onClose={() => setSelectedBroadcast(null)}
            onNavigate={openBroadcast}
            onAcknowledge={() => acknowledgeBroadcast(selectedBroadcast)}
          />,
          document.body,
        )}

      {/* ── Remote-control overlays (portaled to body so they sit above
          everything in the kapp app, regardless of where the widget is
          mounted in the DOM tree). Only visible while the agent is
          actively driving the page. */}
      {remoteControlStatus === "active" && typeof document !== "undefined" && (
        <>
          {createPortal(
            <RemoteControlBanner
              agentName={screenShareAgentName}
              onRevoke={() => revokeRemoteControl("manual")}
            />,
            document.body
          )}
          {createPortal(
            <RemoteCursor position={remoteCursor} />,
            document.body
          )}
        </>
      )}
    </>
  );
};

// ── Live Message bubble ───────────────────────────────────────────────────────
// Backend system messages are written from the agent's perspective. Translate
// the ones that read awkwardly on the customer side into a customer-friendly
// equivalent before rendering.
const CUSTOMER_SYSTEM_MSG_OVERRIDES = {
  "Customer accepted the chat invitation and is now active.":
    "Click Chat Now to respond",
};

function LiveMessage({ msg, brandColor }) {
  if (msg.sender_type === "system") {
    const displayText = CUSTOMER_SYSTEM_MSG_OVERRIDES[msg.message] || msg.message;
    return (
      <div className="khub-system-msg">
        <span>{displayText}</span>
      </div>
    );
  }
  const isUser = msg.sender_type === "user";
  const msgType = msg.message_type || "text";
  return (
    <div className={`khub-live-msg-row ${isUser ? "user-row" : "agent-row"}`}>
      {!isUser && <span className="khub-agent-label">{msg.sender_name}</span>}
      <div
        className={`khub-msg ${isUser ? "user" : "assistant"}`}
        style={isUser ? { background: brandColor } : {}}
      >
        {msgType === "file" && msg.file_url ? (
          <a href={msg.file_url} target="_blank" rel="noreferrer" className="khub-file-link">
            📎 {msg.file_name || "Attachment"}
          </a>
        ) : msgType === "voice" && msg.file_url ? (
          <div>
            <audio controls src={msg.file_url} style={{ maxWidth: "180px", height: "32px" }} />
            <p style={{ fontSize: "11px", margin: "2px 0 0", opacity: 0.8 }}>🎙 Voice message</p>
          </div>
        ) : (
          msg.message
        )}
      </div>
    </div>
  );
}

// ── Broadcast detail panel (K-News) ──────────────────────────────────────────
// Reader modal: BroadcastDetail in a centered overlay with prev/next.
function BroadcastModal({ bc, list, brandColor, onClose, onNavigate, onAcknowledge }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (document.querySelector(".khub-lightbox")) return; // lightbox owns Esc
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const newer = siblingBroadcast(list, bc.id, -1);
  const older = siblingBroadcast(list, bc.id, 1);
  const idx = list.findIndex((b) => b.id === bc.id);

  return (
    <div className="khub-news-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="khub-news-modal"
        role="dialog"
        aria-modal="true"
        aria-label={bc.title}
        onClick={(e) => e.stopPropagation()}
      >
        <BroadcastDetail
          bc={bc}
          brandColor={brandColor}
          onClose={onClose}
          onAcknowledge={onAcknowledge}
        />
        {list.length > 1 && (
          <div className="khub-news-modal-foot">
            <span>{idx + 1} of {list.length}</span>
            <button
              type="button"
              disabled={!newer}
              onClick={() => onNavigate(newer)}
              aria-label="Newer announcement"
            >
              ‹
            </button>
            <button
              type="button"
              disabled={!older}
              onClick={() => onNavigate(older)}
              aria-label="Older announcement"
            >
              ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function BroadcastDetail({ bc, brandColor, onBack, onClose, onAcknowledge }) {
  const needsAck = bc.requires_acknowledgement && !bc.is_acknowledged;
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const handleKey = (e) => {
      if (e.key === "Escape") setLightboxOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [lightboxOpen]);

  const sharedByLabel = bc.created_by_name || "Team Kommerce Hub";
  const sharedByInitial = sharedByLabel.trim().charAt(0).toUpperCase() || "K";

  return (
    <div className="khub-news-detail">
      <div className="khub-news-detail-head">
        {onBack && (
          <button
            className="khub-news-back"
            onClick={onBack}
            aria-label="Back to announcements"
          >
            <IconBack />
          </button>
        )}
        <span
          className={`khub-news-pill khub-news-pill-${bc.priority}`}
        >
          {bc.priority}
        </span>
        {Array.isArray(bc.tags) && bc.tags.map((tag) => (
          <span key={tag} className="khub-news-tag">{tag}</span>
        ))}
        {onClose && (
          <button
            type="button"
            className="khub-news-back khub-news-close"
            onClick={onClose}
            aria-label="Close"
            autoFocus
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {bc.banner_image_url && (
        <button
          type="button"
          className="khub-news-banner-btn"
          onClick={() => setLightboxOpen(true)}
          aria-label="Enlarge image"
        >
          <img
            src={resolveMediaUrl(bc.banner_image_url)}
            alt=""
            className="khub-news-banner"
            onError={(e) => {
              e.currentTarget.parentElement.style.display = "none";
            }}
          />
          <span className="khub-news-banner-zoom" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
              <line x1="11" y1="8" x2="11" y2="14" />
              <line x1="8" y1="11" x2="14" y2="11" />
            </svg>
          </span>
        </button>
      )}

      <h3 className="khub-news-detail-title">{bc.title}</h3>
      <div className="khub-news-detail-author">
        <span
          className="khub-news-detail-avatar"
          style={{ background: brandColor }}
        >
          {sharedByInitial}
        </span>
        <span className="khub-news-detail-author-text">
          Shared by <strong>{sharedByLabel}</strong>
          {bc.sent_at && (
            <>
              <span className="khub-news-detail-dot">•</span>
              {formatBroadcastTime(bc.sent_at)}
            </>
          )}
        </span>
      </div>

      {/* body_html is rendered + sanitised by the CS Portal (services/rich_text)
          so agent-authored HTML arrives display-ready. parseMarkdown is the
          fallback for announcements stored before that renderer existed. */}
      <div
        className="khub-news-detail-body"
        // body_html is sanitised server-side (CS Portal rich_text.render_body);
        // fall back to the local Markdown renderer for pre-046 rows.
        dangerouslySetInnerHTML={{
          __html: bc.body_html || parseMarkdown(bc.body),
        }}
      />

      {Array.isArray(bc.tags) && bc.tags.length > 0 && (
        <div className="khub-news-detail-tags">
          {bc.tags.map((tag) => (
            <span key={tag} className="khub-news-tag">{tag}</span>
          ))}
        </div>
      )}

      {Array.isArray(bc.attachments) && bc.attachments.length > 0 && (
        <div className="khub-news-detail-attachments">
          {bc.attachments.map((a) => (
            <a
              key={a.filename}
              href={resolveAttachmentUrl(a)}
              target="_blank"
              rel="noreferrer"
              className="khub-news-attachment"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round">
                {a.kind === "image"
                  ? <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></>
                  : <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>
                }
              </svg>
              <span className="khub-news-attachment-name">{a.name || a.filename}</span>
              {a.size > 0 && (
                <span className="khub-news-attachment-size">
                  {a.size < 1024 * 1024
                    ? `${(a.size / 1024).toFixed(1)} KB`
                    : `${(a.size / (1024 * 1024)).toFixed(1)} MB`}
                </span>
              )}
            </a>
          ))}
        </div>
      )}

      {bc.cta_url && bc.cta_label && (
        <a
          className="khub-news-cta"
          href={bc.cta_url}
          target="_blank"
          rel="noreferrer"
          style={{ background: brandColor }}
        >
          {bc.cta_label}
        </a>
      )}

      {needsAck && (
        <button
          className="khub-news-ack-btn"
          onClick={onAcknowledge}
          style={{ background: brandColor }}
        >
          I understand
        </button>
      )}
      {bc.requires_acknowledgement && bc.is_acknowledged && (
        <p className="khub-news-ack-done">✓ Acknowledged</p>
      )}

      {lightboxOpen && bc.banner_image_url && (
        <ImageLightbox
          src={resolveMediaUrl(bc.banner_image_url)}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}

// ── Fullscreen image lightbox (used by K-News banner) ───────────────────────
function ImageLightbox({ src, onClose }) {
  return (
    <div
      className="khub-lightbox"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <button
        type="button"
        className="khub-lightbox-close"
        onClick={onClose}
        aria-label="Close image"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round">
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      </button>
      <img
        src={src}
        alt=""
        className="khub-lightbox-img"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// Date+time formatter used in the Recent conversations list. Shows "Today
// 3:45 PM" / "Yesterday 10:12 AM" for fresh sessions and "Apr 13, 2026 ·
// 3:45 PM" for older ones, so the user can pick by date.
function formatSessionDateTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return `Today ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;
  const date = d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  return `${date} · ${time}`;
}

// Relative-time formatter used in K-News list + detail
function formatBroadcastTime(ts) {
  if (!ts) return "";
  const date = new Date(ts);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString();
}

export default KhubChatbotWidget;
